/* eslint-disable react/no-array-index-key */
import {SerializedStyles} from '@emotion/react';
import {Collapse, Typography, css, useTheme} from '@mui/material';
import {
  ArrayUtils,
  ConfigObjectType,
  ConfirmDialog,
  DlMuiButton,
  DlMuiDynamicIcon,
  GetStylesFunction,
  TranslateUtils,
  cleanLocalStorage,
  enumContainer,
  omitNullsy,
  omitUndefined,
  PopupWrapper,
  useApiMutation,
  useApiQuery,
  useCurrentLocale,
  useLocalStorage,
  HierarchyNode,
} from '@wallaby-ui/core';
import {
  HaidiSchema,
  LightAccordion,
  LightDateFilter,
  LightSelectFilter,
  LightTextAreaFilter,
  WallabyCoreSchema,
  getHaiStateIcon,
  getHaiTypeColor,
  useValidate,
  ValidateErrorStrings,
  isValidDateString,
  useFormatters,
  Formatters,
  LightAccordionButtonHeader,
  TooltipWrapper,
  useAgens,
  usePermissions,
  useHaidiGroupDeptsBySecurity,
  LightOrganizationFilter2,
  LightErrorBox,
  NormalizedOption,
  HaidiHaiStateFormattedChip,
  HaidiHaiTypeIconFormattedChip,
  LightHierarchyFilter,
} from '@wallaby-ui/document';
import {isEqual, omit, sortBy} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';

import {useAllTranslations} from './HaiItemUtils';
import {useInvasiveDevices} from './useInvasiveDevices';
import {normalizeFreeSoloStrings, useValueListField} from './useValueListField';
import {useStandardTypes} from './useStandardTypes';
import {useAppSelector} from 'src/js/store_hooks';

interface BaseHAIInfo<StringT, StringArrayT, BooleanT, NumberT> {
  // NOTE: these have to match translations
  type: StringT;
  standardTypeId: NumberT;
  invasiveDevices: StringArrayT;
  invasiveDeviceDate: StringT;
  surgeryTypes: StringArrayT;
  surgeryDate: StringT;
  userTags: StringArrayT;
  department: StringT;
  onsetDepartment: StringT;
  onsetDepartmentLocked: BooleanT;
  date: StringT;
  bsiOrigin: StringT;
  origin: StringT;
  agens: StringArrayT;
  state: StringT;
  reportedBy: StringArrayT;
  checkedByEpi: BooleanT;
  definition: StringArrayT;
  death: StringT;
  comment: StringT;
}
type HAIInfo = BaseHAIInfo<string, string[], boolean, number>;
type DiffRow = string | undefined | null;
type HAIInfoDiff = BaseHAIInfo<DiffRow, DiffRow, DiffRow, DiffRow>;

type HaiPathogenDto = HaidiSchema.components['schemas']['HaiPathogenDto'];
// PDH-3175: onset-department fields the BE adds to HAIDetailDto; drop this extension
// once haidi_schema.ts is regenerated against the new backend.
type HAIDetailDtoWithOnset = HaidiSchema.components['schemas']['HAIDetailDto'] & {
  onsetOrganizationId?: number | null;
  onsetOrganizationLongLabel?: string | null;
  onsetOrganizationLocked?: boolean;
};
type Props = {
  encounterId: string;
  hai: HAIDetailDtoWithOnset | undefined;
  config: ConfigObjectType;
  refetchHais: () => void;
  isNewHai?: boolean;
  cancelAddingHai: () => void;
};

const EMPTY = '—';
const EncounterHaiItem: React.FC<Props> = (props) => {
  const {encounterId, hai, config, refetchHais, isNewHai, cancelAddingHai} = props;
  const haiId = useMemo(() => hai?.id ?? 0, [hai?.id]);
  const translations = useAllTranslations(config);
  const locale = useCurrentLocale();

  const theme = useTheme();

  const {t, tEnum, formatDate, formatString} = useFormatters();
  const storageIdPrefix = useMemo(() => `/encounter/${encounterId}.hai-${haiId}`, [encounterId, haiId]);
  const useStorage = (key: string, defaultValue: any) => {
    const [value, _, setValueAndRerender] = useLocalStorage(`${storageIdPrefix}${key}`, defaultValue);
    return [value, setValueAndRerender];
  };
  const cleanStorage = useCallback(() => {
    cleanLocalStorage(`${storageIdPrefix}.`);
  }, [storageIdPrefix]);
  const [isMaximized, setIsMaximized] = useStorage('.isMaximized', false);

  // edit
  const enumMap = useAppSelector((state) => (state[enumContainer.ENUM_REDUCER_STATE_ID] as any).enumMap);
  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);
  const bsiOriginOptions = useMemo(
    () =>
      sortBy(
        enumMap.BSIOrigin.filter((v) => !v.isDeprecated).map((v) => ({id: v.name, label: `${t(v.messageKey)}`})),
        [(v) => v.label]
      ),
    [enumMap.BSIOrigin, t]
  );
  const haiTypesWithoutUnspecified = useMemo(
    () =>
      sortBy(
        enumMap.HaiTypeEnum.filter((v) => v.name !== 'HAI')
          .filter((v) => !v.isDeprecated)
          .map((v) => ({
            id: v.name,
            label:
              v.name === 'BSI'
                ? (() => {
                    // console.log('BSI enum value:', t(v.messageKey));
                    return t(v.messageKey)
                      .replace(/\s*\([^)]*\)/g, '')
                      .trim();
                  })()
                : t(v.messageKey),
          })),
        [(v) => v.label]
      ),
    [enumMap.HaiTypeEnum, t]
  );
  const departmentsUrl = `${config.service.url.organizationsFilterData}`;
  const {data: departmentsData} = useApiQuery<WallabyCoreSchema.components['schemas']['OrganizationFilterDto'][]>([departmentsUrl], null, {
    axiosConfig: {
      url: departmentsUrl,
    },
  });
  const agensData = useAgens(config);
  const sortedAgensData = useMemo(() => sortBy(agensData ?? [], (v) => v.label), [agensData]);
  const CHECKED_BY_EPI_YES = enumMap.CheckedByEpi.find((v) => v.name === 'YES');
  const CHECKED_BY_EPI_NO = enumMap.CheckedByEpi.find((v) => v.name === 'NO');
  const DEATH_NO = enumMap.HaiDeathEnum.find((v) => v.name === 'NO');
  const [isEditing, _setIsEditing] = useStorage('.isEditing', false);
  const [typeInput, setTypeInput] = useStorage('.typeInput', '');
  const [standardTypeInput, setStandardTypeInput] = useStorage('.standardTypeInput', -1);
  const [standardTypeCodeInput, setStandardTypeCodeInput] = useStorage('.standardTypeCodeInput', '');
  const [invasiveDevicesInput, setInvasiveDevicesInput] = useStorage('.invasiveDevicesInput', []);
  const [departmentInput, setDepartmentInput] = useStorage('.departmentInput', ''); // TODO: future - autocomplete input
  const [onsetDepartmentInput, setOnsetDepartmentInput] = useStorage('.onsetDepartmentInput', '');
  const [onsetDepartmentLockedInput, setOnsetDepartmentLockedInput] = useStorage('.onsetDepartmentLockedInput', true);
  const [bsiOriginInput, setBsiOriginInput] = useStorage('.bsiOriginInput', '');
  const [originInput, setOriginInput] = useStorage('.originInput', '');
  const [dateInput, setDateInput] = useStorage('.dateInput', '');
  const [invasiveDeviceDateInput, setInvasiveDeviceDateInput] = useStorage('.invasiveDeviceDateInput', '');
  const [surgeryTypesInput, setSurgeryTypesInput] = useStorage('.surgeryTypesInput', [] as string[]);
  const [surgeryDateInput, setSurgeryDateInput] = useStorage('.surgeryDateInput', '');
  const [userTagsInput, setUserTagsInput] = useStorage('.userTagsInput', [] as string[]);
  const [agensInput, setAgensInput] = useStorage('.agensInput', [] as string[]); // TODO: future - autocomplete input
  const [stateInput, setStateInput] = useStorage('.stateInput', '');
  const [reportedByInput, setReportedByInput] = useStorage('.reportedByInput', [] as string[]);
  const [checkedByEpiInput, setCheckedByEpiInput] = useStorage('.checkedByEpiInput', false);
  // (readonly rules)
  const [definitionInput, setDefinitionInput] = useStorage('.definitionInput', [] as string[]);
  const [deathInput, setDeathInput] = useStorage('.deathInput', '');
  const [commentInput, setCommentInput] = useStorage('.commentInput', '');

  const {
    isLoading: standardTypesLoading,
    standardType,
    standardTypeOptions,
    getHaiTypeMappingForCode,
    getCodeLabelForCode,
    separatedStandardTypeCode,
    getTranslationForStandardTypeId,
    getCodeLabelForStandardTypeId,
    getParentHaiTypeMappingForCode,
  } = useStandardTypes({config, selectedType: typeInput});
  const {invasiveDeviceOptions, refetchInvasiveDeviceValues, getInvasiveDeviceLabel} = useInvasiveDevices({config});
  const {options: surgeryTypeOptions, refetch: refetchSurgeryTypeValues} = useValueListField({
    config,
    url: config.service.url.surgeryTypeValues,
  });
  const {options: userTagOptions, refetch: refetchUserTagValues} = useValueListField({
    config,
    url: config.service.url.userTagValues,
  });

  // PDH-3175: while the onset department is locked it follows the onset date — any date change
  // re-derives the department from hospitalization/transfer history on the BE.
  const onsetDepartmentUrl = ((config.service.url.haiOnsetDepartment as string) ?? '').replace('{encounterId}', encounterId);
  // the date input may carry a T00:00:00 suffix after a calendar pick (LightDateFilter without
  // dateOnly) — the BE endpoint takes a plain LocalDate, so send the date part only
  const onsetDerivationDate = typeof dateInput === 'string' ? dateInput.slice(0, 10) : dateInput;
  const onsetDerivationEnabled =
    Boolean(isEditing) && Boolean(onsetDepartmentLockedInput) && Boolean(onsetDepartmentUrl) && isValidDateString(dateInput);
  const {data: derivedOnsetDepartment} = useApiQuery<{
    onsetOrganizationId?: number | null;
    onsetOrganizationLongLabel?: string | null;
  }>(
    [onsetDepartmentUrl, onsetDerivationDate],
    null,
    {
      axiosConfig: {url: onsetDepartmentUrl, params: {date: onsetDerivationDate}},
      reactQueryOptions: {enabled: onsetDerivationEnabled},
    },
    true
  );
  useEffect(() => {
    if (!onsetDerivationEnabled || derivedOnsetDepartment === undefined) return;
    const derivedId = derivedOnsetDepartment?.onsetOrganizationId != null ? String(derivedOnsetDepartment.onsetOrganizationId) : '';
    if (derivedId !== onsetDepartmentInput) setOnsetDepartmentInput(derivedId);
  }, [onsetDerivationEnabled, derivedOnsetDepartment, onsetDepartmentInput, setOnsetDepartmentInput]);

  // console.log(standardTypeOptions);
  // console.log(standardTypeCodeInput);
  const haiInfo: HAIInfo = useMemo(() => {
    return {
      type: hai?.type ?? '',
      standardTypeId: hai?.standardTypeId ?? -1,
      invasiveDevices: hai?.invasiveDevices ?? [],
      invasiveDeviceDate: hai?.invasiveDeviceDate ?? '',
      surgeryTypes: hai?.surgeryTypes ?? [],
      surgeryDate: hai?.surgeryDate ?? '',
      userTags: hai?.userTags ?? [],
      department: String(hai?.organizationId ?? ''),
      onsetDepartment: String(hai?.onsetOrganizationId ?? ''),
      onsetDepartmentLocked: hai?.onsetOrganizationLocked ?? true,
      date: hai?.date ?? '',
      bsiOrigin: hai?.bsiOrigin ?? '',
      origin: hai?.origin ?? '',
      agens: sortPathogensByLocale(hai?.pathogens ?? [], locale).map((v) => String(v.id)),
      state: hai?.state ?? '',
      reportedBy: hai?.reporters ?? [],
      checkedByEpi: isNewHai ? false : hai?.checkedByEpi ?? false,
      definition: hai?.definitions ?? [],
      death: isNewHai ? DEATH_NO.name : hai?.death ?? '',
      comment: hai?.comment ?? '',
    };
  }, [hai, locale]);
  // IDEA-290: the read-only view has no separate onset-department row — the department shows as
  // an inline note after the onset date, only when it differs from the source department (or as
  // Unknown when unavailable).
  const onsetDepartmentNote = useMemo(() => {
    if (isNewHai || !hai) return null;
    if (hai.onsetOrganizationId == null) return t('hai.miniScreen.hai.date.onsetDepartmentUnknownNote');
    if (String(hai.onsetOrganizationId) === String(hai.organizationId ?? '')) return null;
    return formatString(t('hai.miniScreen.hai.date.onsetDepartmentNote'), {
      department: hai.onsetOrganizationLongLabel ?? String(hai.onsetOrganizationId),
    });
  }, [isNewHai, hai, t, formatString]);
  const startEditing = useCallback(() => {
    setTypeInput(haiInfo.type);
    setStandardTypeInput(haiInfo.standardTypeId);
    const foundOption = standardTypeOptions.find((opt) => opt.original?.id === haiInfo.standardTypeId);
    setStandardTypeCodeInput(foundOption?.id ?? '');
    setDepartmentInput(haiInfo.department);
    setOnsetDepartmentInput(haiInfo.onsetDepartment);
    setOnsetDepartmentLockedInput(haiInfo.onsetDepartmentLocked);
    setDateInput(haiInfo.date);
    setInvasiveDevicesInput(haiInfo.invasiveDevices);
    setInvasiveDeviceDateInput(haiInfo.invasiveDeviceDate);
    setSurgeryTypesInput(haiInfo.surgeryTypes);
    setSurgeryDateInput(haiInfo.surgeryDate);
    setUserTagsInput(haiInfo.userTags);
    setBsiOriginInput(haiInfo.bsiOrigin);
    setOriginInput(haiInfo.origin);
    setAgensInput(haiInfo.agens);
    setStateInput(haiInfo.state);
    setReportedByInput(haiInfo.reportedBy);
    setCheckedByEpiInput(haiInfo.checkedByEpi);
    setDefinitionInput(haiInfo.definition);
    setDeathInput(haiInfo.death);
    setCommentInput(haiInfo.comment);
    _setIsEditing(true);
    setIsMaximized(true);
  }, [
    setTypeInput,
    haiInfo.type,
    haiInfo.standardTypeId,
    haiInfo.department,
    haiInfo.onsetDepartment,
    haiInfo.onsetDepartmentLocked,
    haiInfo.date,
    haiInfo.invasiveDevices,
    haiInfo.invasiveDeviceDate,
    haiInfo.surgeryTypes,
    haiInfo.surgeryDate,
    haiInfo.userTags,
    haiInfo.bsiOrigin,
    haiInfo.origin,
    haiInfo.agens,
    haiInfo.state,
    haiInfo.reportedBy,
    haiInfo.checkedByEpi,
    haiInfo.definition,
    haiInfo.death,
    haiInfo.comment,
    setStandardTypeInput,
    standardTypeOptions,
    setStandardTypeCodeInput,
    setDepartmentInput,
    setOnsetDepartmentInput,
    setOnsetDepartmentLockedInput,
    setDateInput,
    setInvasiveDevicesInput,
    setInvasiveDeviceDateInput,
    setSurgeryTypesInput,
    setSurgeryDateInput,
    setUserTagsInput,
    setBsiOriginInput,
    setOriginInput,
    setAgensInput,
    setStateInput,
    setReportedByInput,
    setCheckedByEpiInput,
    setDefinitionInput,
    setDeathInput,
    setCommentInput,
    _setIsEditing,
    setIsMaximized,
    isNewHai,
  ]);
  const stopEditingAndRefetchHais = useCallback(
    (newData: any) => {
      if (isNewHai) {
        cancelAddingHai();
        cleanStorage();
      } else {
        _setIsEditing(false);
        cleanStorage();
      }
      localStorage.setItem(`/encounter/${encounterId}.hai-${newData?.id}.isMaximized`, 'true');
      refetchHais();
      refetchInvasiveDeviceValues();
      refetchSurgeryTypeValues();
      refetchUserTagValues();
    },
    [
      isNewHai,
      encounterId,
      refetchHais,
      refetchInvasiveDeviceValues,
      refetchSurgeryTypeValues,
      refetchUserTagValues,
      cancelAddingHai,
      cleanStorage,
      _setIsEditing,
    ]
  );
  useEffect(() => {
    if (isNewHai && !isEditing) startEditing();
  }, [isNewHai, isEditing, startEditing]);
  const newHaiInfo: {
    type: any;
    standardTypeId: any;
    invasiveDevices: string[];
    invasiveDeviceDate: any;
    surgeryTypes: string[];
    surgeryDate: any;
    userTags: string[];
    department: any;
    onsetDepartment: any;
    onsetDepartmentLocked: any;
    date: any;
    bsiOrigin: any;
    origin: any;
    agens: any;
    state: any;
    reportedBy: any;
    checkedByEpi: any;
    definition: any;
    death: any;
    comment: any;
  } = useMemo(() => {
    // Surgery type/date are SSI-only and the date requires at least one type. Gate them here so newHaiInfo
    // is the single source of truth: the diff, unsaved-changes check and save payload all stay consistent
    // no matter which path changed the type (direct select, standard-type-driven, inconsistent-type confirm).
    const isSsi = typeInput === 'SSI';
    // Mirror the BE value-list cleaning (trim + drop blank/whitespace) so we never persist blank-only
    // values, and — crucially — never send a date alongside a blank-only value list: the BE rejects an
    // invasive-device / surgery date (400) unless at least one NON-BLANK value is present.
    const cleanValueList = (values: any): string[] =>
      normalizeFreeSoloStrings(values)
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
    const normalizedInvasiveDevices = cleanValueList(invasiveDevicesInput);
    const normalizedSurgeryTypes = isSsi ? cleanValueList(surgeryTypesInput) : [];
    return {
      type: typeInput ?? '',
      standardTypeId: standardTypeInput ?? -1,
      // Merged value list: predefined devices carried as enum NAME strings alongside free text.
      invasiveDevices: normalizedInvasiveDevices,
      invasiveDeviceDate:
        normalizedInvasiveDevices.length > 0 && isValidDateString(invasiveDeviceDateInput) ? invasiveDeviceDateInput : '',
      surgeryTypes: normalizedSurgeryTypes,
      surgeryDate:
        normalizedSurgeryTypes.length > 0 && isValidDateString(surgeryDateInput) ? surgeryDateInput : '',
      // User-defined tags: raw-text value list, allowed on every HAI type (no gating).
      userTags: cleanValueList(userTagsInput),
      department: departmentInput ?? '',
      onsetDepartment: onsetDepartmentInput ?? '',
      onsetDepartmentLocked: onsetDepartmentLockedInput ?? true,
      date: isValidDateString(dateInput) ? dateInput : '',
      bsiOrigin: bsiOriginInput ?? '',
      origin: originInput ?? '',
      agens: agensInput ?? [],
      state: stateInput ?? '',
      reportedBy: reportedByInput ?? [],
      checkedByEpi: checkedByEpiInput ?? '',
      definition: definitionInput ?? [],
      death: deathInput ?? '',
      comment: commentInput ?? '',
    };
  }, [
    invasiveDevicesInput,
    invasiveDeviceDateInput,
    surgeryTypesInput,
    surgeryDateInput,
    userTagsInput,
    typeInput,
    standardTypeInput,
    departmentInput,
    onsetDepartmentInput,
    onsetDepartmentLockedInput,
    dateInput,
    bsiOriginInput,
    originInput,
    agensInput,
    stateInput,
    reportedByInput,
    checkedByEpiInput,
    definitionInput,
    deathInput,
    commentInput,
  ]);
  const haiInfoDiff: HAIInfoDiff = useMemo(() => {
    const acc: HAIInfoDiff = {
      type: undefined,
      standardTypeId: undefined,
      invasiveDevices: undefined,
      invasiveDeviceDate: undefined,
      surgeryTypes: undefined,
      surgeryDate: undefined,
      userTags: undefined,
      department: undefined,
      onsetDepartment: undefined,
      onsetDepartmentLocked: undefined,
      date: undefined,
      bsiOrigin: undefined,
      origin: undefined,
      agens: undefined,
      state: undefined,
      reportedBy: undefined,
      checkedByEpi: undefined,
      definition: undefined,
      death: undefined,
      comment: undefined,
    };
    if (newHaiInfo.type !== haiInfo.type) acc.type = tEnum('HaiTypeEnum', newHaiInfo.type);
    if (newHaiInfo.standardTypeId !== haiInfo.standardTypeId)
      acc.standardTypeId = newHaiInfo.standardTypeId ? getTranslationForStandardTypeId(newHaiInfo.standardTypeId) : '';
    if (!isEqual(newHaiInfo.invasiveDevices, haiInfo.invasiveDevices)) {
      acc.invasiveDevices = newHaiInfo.invasiveDevices.map(getInvasiveDeviceLabel).join(', ') || EMPTY;
    }

    if (newHaiInfo.invasiveDeviceDate !== haiInfo.invasiveDeviceDate) acc.invasiveDeviceDate = formatDate(newHaiInfo.invasiveDeviceDate);
    if (!isEqual(newHaiInfo.surgeryTypes, haiInfo.surgeryTypes)) acc.surgeryTypes = newHaiInfo.surgeryTypes.join(', ') || EMPTY;
    if (newHaiInfo.surgeryDate !== haiInfo.surgeryDate) acc.surgeryDate = formatDate(newHaiInfo.surgeryDate);
    if (!isEqual(newHaiInfo.userTags, haiInfo.userTags)) acc.userTags = newHaiInfo.userTags.join(', ') || EMPTY;
    if (newHaiInfo.department !== haiInfo.department)
      acc.department =
        (departmentsData ?? []).find((v) => String(v.id) === String(newHaiInfo.department))?.organizationLongName ??
        String(newHaiInfo.department);
    if (newHaiInfo.onsetDepartment !== haiInfo.onsetDepartment)
      acc.onsetDepartment = newHaiInfo.onsetDepartment
        ? (departmentsData ?? []).find((v) => String(v.id) === String(newHaiInfo.onsetDepartment))?.organizationLongName ??
          String(newHaiInfo.onsetDepartment)
        : t('hai.miniScreen.hai.onsetDepartment.unknown');
    if (newHaiInfo.onsetDepartmentLocked !== haiInfo.onsetDepartmentLocked)
      acc.onsetDepartmentLocked = t(newHaiInfo.onsetDepartmentLocked ? 'hai.miniScreen.option.value.yes' : 'hai.miniScreen.option.value.no');
    if (newHaiInfo.date !== haiInfo.date) acc.date = formatDate(newHaiInfo.date);
    if (newHaiInfo.bsiOrigin !== haiInfo.bsiOrigin) acc.bsiOrigin = tEnum('BSIOrigin', newHaiInfo.bsiOrigin);
    if (newHaiInfo.origin !== haiInfo.origin) acc.origin = tEnum('Origin', newHaiInfo.origin);
    if (!isEqual(newHaiInfo.agens, haiInfo.agens))
      acc.agens = newHaiInfo.agens.map((id) => (agensData ?? []).find((v) => String(v.id) === String(id))?.label ?? 'null').join(', ');
    if (newHaiInfo.state !== haiInfo.state) acc.state = tEnum('State', newHaiInfo.state);
    if (!isEqual(newHaiInfo.reportedBy, haiInfo.reportedBy))
      acc.reportedBy = newHaiInfo.reportedBy.map((id) => tEnum('Reporter', id)).join(', ');
    if (newHaiInfo.checkedByEpi !== haiInfo.checkedByEpi)
      acc.checkedByEpi = tEnum('CheckedByEpi', newHaiInfo.checkedByEpi ? CHECKED_BY_EPI_YES.name : CHECKED_BY_EPI_NO.name);
    if (!isEqual(newHaiInfo.definition, haiInfo.definition))
      acc.definition = newHaiInfo.definition.map((id) => tEnum('Definition', id)).join(', ');
    if (newHaiInfo.death !== haiInfo.death) acc.death = tEnum('HaiDeathEnum', newHaiInfo.death);
    if (newHaiInfo.comment !== haiInfo.comment) acc.comment = newHaiInfo.comment;
    return omitUndefined(acc);
  }, [
    newHaiInfo.type,
    newHaiInfo.standardTypeId,
    newHaiInfo.invasiveDevices,
    newHaiInfo.invasiveDeviceDate,
    newHaiInfo.surgeryTypes,
    newHaiInfo.surgeryDate,
    newHaiInfo.userTags,
    newHaiInfo.department,
    newHaiInfo.onsetDepartment,
    newHaiInfo.onsetDepartmentLocked,
    newHaiInfo.date,
    newHaiInfo.bsiOrigin,
    newHaiInfo.origin,
    newHaiInfo.agens,
    newHaiInfo.state,
    newHaiInfo.reportedBy,
    newHaiInfo.checkedByEpi,
    newHaiInfo.definition,
    newHaiInfo.death,
    newHaiInfo.comment,
    haiInfo.type,
    haiInfo.standardTypeId,
    haiInfo.invasiveDevices,
    haiInfo.invasiveDeviceDate,
    haiInfo.surgeryTypes,
    haiInfo.surgeryDate,
    haiInfo.userTags,
    haiInfo.department,
    haiInfo.onsetDepartment,
    haiInfo.onsetDepartmentLocked,
    haiInfo.date,
    haiInfo.bsiOrigin,
    haiInfo.origin,
    haiInfo.agens,
    haiInfo.state,
    haiInfo.reportedBy,
    haiInfo.checkedByEpi,
    haiInfo.definition,
    haiInfo.death,
    haiInfo.comment,
    t,
    tEnum,
    getInvasiveDeviceLabel,
    getTranslationForStandardTypeId,
    formatDate,
    departmentsData,
    CHECKED_BY_EPI_YES.name,
    CHECKED_BY_EPI_NO.name,
    agensData,
  ]);
  const haveUnsavedChanges = useMemo(() => {
    return !isEqual(newHaiInfo, haiInfo);
  }, [newHaiInfo, haiInfo]);

  const [confirmDeleteOpened, setConfirmDeleteOpened] = useState(false);
  const deleteHaiUrl = config.service.url.haisDelete.replace('{encounterId}', encounterId).replace('{haiId}', haiId);
  const {mutate: mutateDeleteHai} = useApiMutation(
    [deleteHaiUrl],
    null,
    {
      reactMutationOptions: {
        onSuccess: stopEditingAndRefetchHais,
        // onError,
      },
      axiosConfig: {
        method: 'DELETE',
        url: deleteHaiUrl,
      },
    },
    true
  );
  const onConfirmDelete = useCallback(() => {
    if (isNewHai) {
      cancelAddingHai();
      cleanStorage();
    } else {
      mutateDeleteHai();
    }
    setConfirmDeleteOpened(false);
  }, [isNewHai, cancelAddingHai, mutateDeleteHai, cleanStorage]);
  const onCancelDelete = useCallback(() => {
    setConfirmDeleteOpened(false);
  }, []);

  const [confirmResetOpened, setConfirmResetOpened] = useState(false);
  const resetAndClose = useCallback(() => {
    if (isNewHai) {
      cancelAddingHai();
      cleanStorage();
    } else {
      _setIsEditing(false);
      cleanStorage();
      refetchHais();
    }
  }, [isNewHai, cancelAddingHai, _setIsEditing, cleanStorage, refetchHais]);
  const onConfirmReset = useCallback(() => {
    resetAndClose();
    setConfirmResetOpened(false);
  }, [resetAndClose]);
  const onCancelReset = useCallback(() => {
    setConfirmResetOpened(false);
  }, []);

  const editHaiUrl = config.service.url.haisUpdate.replace('{encounterId}', encounterId).replace('{haiId}', haiId);
  const commentNullValue = useMemo(() => {
    let accCommentNull = '' as string | null;
    if (!isNewHai && (hai?.comment === undefined || hai?.comment === null)) {
      accCommentNull = null;
    }
    return accCommentNull;
  }, [isNewHai, hai]);
  const editHaiData = useMemo(() => {
    const isSsi = newHaiInfo.type === 'SSI';
    const data = omitNullsy({
      // Legacy invasive-device split fields were merged into `invasiveDevices` (PDH-3128); never echo them
      // back from the GET response into the write payload.
      ...omit(hai ?? {}, ['id', 'invasiveDevicesPredefined', 'invasiveDevicesUserDefined', 'onsetOrganizationLongLabel']),
      type: newHaiInfo.type,
      standardTypeId: newHaiInfo.standardTypeId === -1 || newHaiInfo.standardTypeId == null ? undefined : newHaiInfo.standardTypeId,
      invasiveDevices: newHaiInfo.invasiveDevices,
      invasiveDeviceDate: newHaiInfo.invasiveDeviceDate || undefined,
      // SSI-only surgery fields; for non-SSI they are deleted from the payload entirely (see below).
      surgeryTypes: newHaiInfo.surgeryTypes,
      surgeryDate: newHaiInfo.surgeryDate || undefined,
      userTags: newHaiInfo.userTags,
      bsiOrigin: newHaiInfo.type === 'BSI' ? newHaiInfo.bsiOrigin || undefined : undefined,
      organizationId: newHaiInfo.department,
      // PDH-3175: null onset organization means "unknown" (there is no Unknown organization row);
      // omitNullsy drops the null key, which the BE reads the same way.
      onsetOrganizationId: newHaiInfo.onsetDepartment ? Number(newHaiInfo.onsetDepartment) : null,
      onsetOrganizationLocked: newHaiInfo.onsetDepartmentLocked,
      date: newHaiInfo.date,
      state: newHaiInfo.state,
      checkedByEpi: newHaiInfo.checkedByEpi,
      death: newHaiInfo.death || undefined,
      origin: newHaiInfo.origin || undefined,
      comment: newHaiInfo.comment ? newHaiInfo.comment : commentNullValue,
      reporters: newHaiInfo.reportedBy,
      pathogens: newHaiInfo.agens.map((id) => (agensData ?? []).find((v) => String(v.id) === String(id))),
      definitions: newHaiInfo.definition,
    });
    // Surgery type/date are SSI-only: for a non-SSI HAI omit them from the payload entirely (not even
    // surgeryTypes: []). The BE DTO defaults `surgeryTypes = emptySet()`, so a missing key clears them
    // exactly like [] would — this still satisfies "the FE must clear surgery fields when switching an SSI
    // HAI to another type". We DELETE explicitly because the `...omit(hai)` spread above carries the
    // pre-edit surgeryTypes/surgeryDate; leaving them in would resend the old values and trip the SSI-only
    // 400 guard.
    if (!isSsi) {
      delete (data as any).surgeryTypes;
      delete (data as any).surgeryDate;
    }
    return data;
  }, [newHaiInfo, agensData, hai, commentNullValue]);
  const [isSaving, setIsSaving] = useState(false);
  const {mutate: mutateEditHai} = useApiMutation(
    [editHaiUrl, editHaiData],
    editHaiData,
    {
      reactMutationOptions: {
        onSuccess: (data) => {
          setIsSaving(false);
          stopEditingAndRefetchHais(data);
        },
        onError: () => {
          setIsSaving(false);
        },
      },
      axiosConfig: {
        data: editHaiData,
        method: 'PATCH',
        url: editHaiUrl,
      },
    },
    true
  );
  const createHaiUrl = config.service.url.hais.replace('{encounterId}', encounterId);
  const {mutate: mutateCreateHai} = useApiMutation(
    [createHaiUrl, editHaiData],
    editHaiData,
    {
      reactMutationOptions: {
        onSuccess: stopEditingAndRefetchHais,
        // onError,
      },
      axiosConfig: {
        data: editHaiData,
        method: 'POST',
        url: createHaiUrl,
      },
    },
    true
  );
  const {errors, haveErrors, validate} = useValidate(() => {
    const err: ValidateErrorStrings = {};
    const isNotRejected = stateInput !== 'NO';
    if (!typeInput || (typeInput === 'HAI' && isNotRejected)) err.type = t('hai.miniScreen.hai.type.validation.required');
    if (!departmentInput) err.department = t('hai.miniScreen.hai.department.validation.required');
    if (!dateInput) err.date = t('hai.miniScreen.hai.date.validation.required');
    if (!stateInput) err.state = t('hai.miniScreen.hai.state.validation.required');
    return err;
  });
  const muiStyles = getStyles({theme, params: {isEditing}});
  const haiTypeToDisplay = isEditing ? newHaiInfo.type : haiInfo.type;
  const getHaiItemHeaderLabel = () => {
    let typeToDisplay = translations.typeTranslation(haiTypeToDisplay as any) ?? '';
    if (haiTypeToDisplay === 'BSI') {
      typeToDisplay = typeToDisplay.replace(/\s*\([^)]*\)/g, '').trim();
    }
    const haiStandardTypeIdToDisplay = isEditing ? newHaiInfo.standardTypeId : haiInfo.standardTypeId;
    let haiStandardTypeToDisplay =
      standardTypeCodeInput != null && haiStandardTypeIdToDisplay != null && haiStandardTypeIdToDisplay !== -1
        ? `(${getCodeLabelForStandardTypeId(haiStandardTypeIdToDisplay)})`
        : '';
    haiStandardTypeToDisplay = haiStandardTypeToDisplay === '(undefined)' ? '' : haiStandardTypeToDisplay;
    return `${typeToDisplay} ${haiStandardTypeToDisplay}`;
  };

  const haiStateToDisplay = isEditing ? newHaiInfo.state : haiInfo.state;
  const colors = getHaiTypeColor({type: haiTypeToDisplay || 'INITIAL', state: haiStateToDisplay});
  const stateIcon = getHaiStateIcon({state: haiStateToDisplay || 'INITIAL'});
  const {userRoles, keycloakTokenParsed} = usePermissions(config);
  const {haidiGroupDeptsBySecurity} = useHaidiGroupDeptsBySecurity(config);
  const currentUser_userName = keycloakTokenParsed.current?.preferred_username;
  const canDeleteHai = !isNewHai && hai?.createdBy && currentUser_userName === hai?.createdBy;
  const viewBasedOnChips = useMemo(
    () =>
      getTextChips(
        (hai?.rules ?? []).map((a) => {
          return {text: translations.ruleTranslation(a), tooltip: translations.ruleTooltipsTranslation(a, hai?.ssiPeriodDays)};
        }),
        muiStyles.haiChip,
        `hai-${haiId}-basedOn`,
        muiStyles.haiState
      ),
    [hai?.rules, hai?.ssiPeriodDays, haiId, muiStyles.haiChip, muiStyles.haiState, translations]
  );
  const editBasedOnRenderTag = useCallback(
    (option: {optionId: string; label: string; original: any}) => {
      const {optionId} = option;
      return renderTextTooltipChip(
        optionId,
        {
          text: translations.ruleTranslation(optionId as any),
          tooltip: translations.ruleTooltipsTranslation(optionId, hai?.ssiPeriodDays),
        },
        muiStyles.haiChip,
        muiStyles.haiState
      );
    },
    [hai?.ssiPeriodDays, muiStyles.haiChip, muiStyles.haiState, translations]
  );
  const getNodeIcon = useCallback(
    (node: HierarchyNode<NormalizedOption>) => {
      return standardTypesLoading ? null : (
        <span
          style={{
            display: 'inline-block',
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: `${getHaiTypeColor({type: node.original.original.haiTypeMapping, state: stateInput}).background}`,
          }}
        />
      );
    },
    [stateInput, standardTypesLoading]
  );
  const getNodeSeparator = useCallback(
    (node: HierarchyNode<NormalizedOption>) => {
      return node.id === separatedStandardTypeCode ? <hr /> : null;
    },
    [separatedStandardTypeCode]
  );
  useEffect(() => {
    if (standardTypeInput !== -1 && standardTypeOptions.length > 0) {
      const foundOption = standardTypeOptions.find((opt) => opt.original?.id === standardTypeInput);
      if (foundOption && foundOption.id !== standardTypeCodeInput) {
        setStandardTypeCodeInput(foundOption.id ?? '');
      }
    }
  }, [standardTypeOptions, standardTypeInput, setStandardTypeCodeInput, standardTypeCodeInput]);
  const [confirmInconsistentTypeOpened, setConfirmInconsistentTypeOpened] = useState<{
    opened: boolean;
    lastConsistentType?: string;
  }>({opened: false});
  const onConfirmInconsistentType = useCallback(() => {
    setStandardTypeCodeInput(undefined);
    setStandardTypeInput(undefined);
    setConfirmInconsistentTypeOpened({opened: false, lastConsistentType: undefined});
  }, [setStandardTypeCodeInput, setStandardTypeInput]);
  const onCancelInconsistentType = useCallback(() => {
    // console.log('setting last consistent type', confirmInconsistentTypeOpened.lastConsistentType);
    setTypeInput(confirmInconsistentTypeOpened.lastConsistentType);
    setConfirmInconsistentTypeOpened({opened: false, lastConsistentType: undefined});
  }, [setTypeInput, confirmInconsistentTypeOpened.lastConsistentType]);
  // PDH-3175: onset-department lock. Unlocking makes the field manually editable; re-locking
  // resumes automatic derivation (the derivation effect immediately restores the derived value
  // for the current onset date). Both directions require an explicit confirmation — shown as the
  // anchored PopupWrapper confirm used in MERIE, pointing at the lock icon.
  const [onsetLockConfirm, setOnsetLockConfirm] = useState<{anchor: HTMLElement; action: 'unlock' | 'relock'} | null>(null);
  const onConfirmOnsetLockToggle = useCallback(() => {
    setOnsetDepartmentLockedInput(onsetLockConfirm?.action !== 'unlock');
    setOnsetLockConfirm(null);
  }, [onsetLockConfirm, setOnsetDepartmentLockedInput]);
  const onCancelOnsetLockToggle = useCallback(() => {
    setOnsetLockConfirm(null);
  }, []);
  const isDeathDefaultForExisting = !isNewHai && deathInput === haiInfo.death;
  const isCheckedByEpiDefaultForExisting = !isNewHai && checkedByEpiInput === haiInfo.checkedByEpi;
  return (
    <LightAccordion
      data-name='encounter-hai-item'
      data-id={haiId}
      isOpen={isMaximized}
      setIsOpen={setIsMaximized}
      disabled={isEditing}
      headerStyle={{
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        color: colors.text,
        background: colors.background,
      }}
      style={{
        borderRadius: '8px',
        paddingBottom: '8px',
        background: isEditing ? '#F2F4F8' : undefined,
      }}
      title={
        <div css={muiStyles.accordionHeaderLeft}>
          <Typography
            data-name='hai-type-header'
            variant='basicTextBold'
            fontSize='14px'
            css={muiStyles.accordionHeaderTypeText}
            style={{color: colors.text}}
          >
            {getHaiItemHeaderLabel()}
          </Typography>
          <div css={muiStyles.accordionHeaderLeftRight}>
            {stateIcon.name && (
              <TooltipWrapper
                tooltip={translations.stateTranslation(haiStateToDisplay as any)}
                noPadding
                style={{
                  flex: 1,
                  maxWidth: 'calc(100% - 32px)',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
                wrapperCss={muiStyles.haiState}
                data-name='hai-state-header'
              >
                <DlMuiDynamicIcon
                  iconName={stateIcon.name}
                  useFixedViewBox={stateIcon.viewbox}
                  style={{color: colors.text, padding: 0}}
                  svgIconInputCss={muiStyles.haiStateIcon}
                  noButtonStyles
                />
                <Typography
                  variant='basicTextBold'
                  fontSize='14px'
                  css={muiStyles.haiStateText}
                  style={{color: colors.text, lineHeight: '1em'}}
                >
                  {translations.stateTranslation(haiStateToDisplay as any)}
                </Typography>
              </TooltipWrapper>
            )}
            <DlMuiDynamicIcon
              data-name='hai-edit-button'
              data-editing={isEditing}
              iconName='penToSquare'
              svgIconInputCss={muiStyles.haiEditIcon}
              onClick={startEditing}
              disabled={isEditing || !userRoles.HAI_EDIT_ROLE}
              iconTitle={'hai.miniScreen.hai.edit.button'}
            />
          </div>
        </div>
      }
      pinnedContent={
        isEditing ? (
          <>
            <div css={muiStyles.haiEditRow}>
              <LightSelectFilter
                config={{
                  name: `hai-${haiId}-Type`,
                  ui: {label: 'hai.miniScreen.hai.type'},
                }}
                options={haiTypesWithoutUnspecified}
                value={typeInput}
                onChange={(newValue) => {
                  if (standardTypeCodeInput) {
                    const consistentType = getParentHaiTypeMappingForCode(standardTypeCodeInput);
                    if (consistentType !== newValue) {
                      setConfirmInconsistentTypeOpened({opened: true, lastConsistentType: consistentType});
                    }
                  }
                  // console.log('onchange -> ', newValue);
                  setTypeInput(newValue);
                }}
                placeholderText={''}
                showChipsInSingleSelect
                renderTag={(info) => (
                  <HaidiHaiTypeIconFormattedChip
                    value={info.optionId}
                    state={stateInput}
                    showAsText
                    showTooltip={false}
                    textColor={'var(--dlAgGridFontColor)'}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} style={{paddingLeft: '3px'}}>
                    <HaidiHaiTypeIconFormattedChip
                      value={(option as NormalizedOption)?.id ?? option}
                      state={stateInput}
                      showAsText
                      showTooltip={false}
                      textColor={'var(--dlAgGridFontColor)'}
                    />
                  </li>
                )}
                error={errors.type}
                isDefaultValue={haiInfoDiff.type === undefined}
              />
            </div>
            {standardType !== 'NO_TYPES' && (
              <div css={muiStyles.haiEditRow}>
                <LightHierarchyFilter
                  config={{
                    name: `hai-${haiId}-StandardType`,
                    ui: {label: `hai.miniScreen.hai.standardType.${standardType}`},
                  }}
                  getNodeIcon={(params) => getNodeIcon(params.node)}
                  optional
                  options={standardTypeOptions}
                  value={standardTypeCodeInput}
                  onChange={(newValue) => {
                    const foundOption = standardTypeOptions.find((opt) => opt.id === newValue);
                    setStandardTypeInput(foundOption?.original?.id ?? -1);
                    setStandardTypeCodeInput(newValue);

                    if (foundOption != null && foundOption.original.haiTypeMapping !== typeInput) {
                      setTypeInput(foundOption.original.haiTypeMapping);
                    }
                  }}
                  renderOption={(props, option) => {
                    return (
                      <li {...props} style={{paddingLeft: '3px'}}>
                        <HaidiHaiTypeIconFormattedChip
                          value={getHaiTypeMappingForCode(option.id)}
                          valueTranslated={option.label}
                          state={stateInput}
                          showAsText
                          showTooltip
                          useEllipsis
                          textColor={'var(--dlAgGridFontColor)'}
                        />
                      </li>
                    );
                  }}
                  renderTag={(info) => {
                    return info.label === '' && info.optionId === '' ? (
                      <span />
                    ) : (
                      <HaidiHaiTypeIconFormattedChip
                        value={getHaiTypeMappingForCode(info.optionId)}
                        valueTranslated={info.label}
                        state={stateInput}
                        showAsText
                        showTooltip
                        useEllipsis
                        textColor={'var(--dlAgGridFontColor)'}
                      />
                    );
                  }}
                  getNodeSeparator={(params) => getNodeSeparator(params.node)}
                  defaultIsExpanded
                  showChipsInSingleSelect
                  placeholderText={''}
                  loading={standardTypesLoading}
                  error={errors.standardTypeId}
                  isDefaultValue={haiInfoDiff.standardTypeId === undefined}
                />
              </div>
            )}
            {typeInput === 'BSI' && (
              <div css={muiStyles.haiEditRow}>
                <LightSelectFilter
                  config={{
                    name: `hai-${haiId}-BsiOrigin`,
                    ui: {
                      label: 'hai.miniScreen.hai.bsiOrigin',
                      placeholder: 'hai.miniScreen.hai.bsiOrigin',
                    },
                  }}
                  options={bsiOriginOptions}
                  value={bsiOriginInput}
                  onChange={(newValue) => {
                    setBsiOriginInput(newValue);
                  }}
                  error={errors.bsiOrigin}
                  isDefaultValue={haiInfoDiff.bsiOrigin === undefined}
                  optional
                />
              </div>
            )}
            <div css={muiStyles.haiEditRow}>
              <LightSelectFilter
                config={{
                  name: `hai-${haiId}-State`,
                  ui: {label: 'hai.miniScreen.hai.state'},
                }}
                options={sortByMessageKey(enumMap.State)}
                value={stateInput}
                onChange={(newValue) => {
                  setStateInput(newValue);
                }}
                placeholderText={''}
                showChipsInSingleSelect
                renderTag={(info) => (
                  <HaidiHaiStateFormattedChip
                    value={info.optionId}
                    state={info.optionId}
                    showAsText
                    showTooltip={false}
                    textColor={'var(--dlAgGridFontColor)'}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} style={{paddingLeft: '3px'}}>
                    <HaidiHaiStateFormattedChip
                      value={(option as NormalizedOption)?.id ?? option}
                      state={(option as NormalizedOption)?.id ?? option}
                      showAsText
                      showTooltip={false}
                      textColor={'var(--dlAgGridFontColor)'}
                    />
                  </li>
                )}
                error={errors.state}
                isDefaultValue={haiInfoDiff.state === undefined}
              />
            </div>
            <div css={muiStyles.haiEditRow}>
              <TooltipWrapper tooltip={t('hai.miniScreen.hai.department.tooltip')} placement='top-start' noPadding style={{width: '100%'}}>
                <LightOrganizationFilter2
                  appConfig={config}
                  preferOrgSecurityDepartments={haidiGroupDeptsBySecurity}
                  // the TooltipWrapper breaks the row's width rule, so without an explicit
                  // maxWidth the select would fall back to its fixed 362px default width
                  maxWidth={'100%'}
                  config={{
                    name: `hai-${haiId}-Department`,
                    ui: {
                      label: departmentInput?.length ? 'hai.miniScreen.hai.department' : 'hai.miniScreen.hai.department',
                      placeholder: departmentInput?.length ? 'hai.miniScreen.hai.department' : 'hai.miniScreen.hai.department',
                    },
                  }}
                  multi={false}
                  value={departmentInput}
                  onChange={(newValue) => {
                    setDepartmentInput(newValue);
                  }}
                  error={errors.department}
                  isDefaultValue={haiInfoDiff.department === undefined}
                  getOptionDisabled={(option) => {
                    return option.levelType !== 'DEPARTMENT';
                  }}
                />
              </TooltipWrapper>
            </div>
            {/* Onset date + onset department share one row (IDEA-290): the department is derived
                for the date, with its lock control rendered inside the field. */}
            <div css={muiStyles.haiEditRow}>
              <div css={muiStyles.onsetDateDepartmentRow} data-name={`hai-${haiId}-onsetDateDepartment-row`}>
                <TooltipWrapper
                  tooltip={t('hai.miniScreen.hai.date.tooltip')}
                  placement='top-start'
                  noPadding
                  style={{flex: '1 1 38%', minWidth: 0}}
                >
                  <LightDateFilter
                    style={{maxWidth: '100%', width: '100%'}}
                    config={{
                      name: `hai-${haiId}-Date`,
                      ui: {label: 'hai.miniScreen.hai.date', placeholder: 'hai.miniScreen.hai.date'},
                    }}
                    value={dateInput}
                    onChange={(newValue) => {
                      setDateInput(newValue ?? '');
                    }}
                    error={errors.date}
                    isDefaultValue={haiInfoDiff.date === undefined}
                    placement='bottom-start'
                  />
                </TooltipWrapper>
                <div
                  css={muiStyles.onsetDepartmentField}
                  data-locked={onsetDepartmentLockedInput ? 'true' : 'false'}
                  data-unknown={Boolean(onsetDepartmentLockedInput) && !onsetDepartmentInput ? 'true' : 'false'}
                  data-name={`hai-${haiId}-onsetDepartment-field`}
                >
                  {/* placement top: while only hovering, the tooltip must not cover the dropdown
                      options below the field. opening the dropdown hides it altogether. */}
                  <TooltipWrapper
                    tooltip={t('hai.miniScreen.hai.onsetDepartment.tooltip')}
                    placement='top-end'
                    noPadding
                    style={{width: '100%'}}
                  >
                    <LightOrganizationFilter2
                      appConfig={config}
                      preferOrgSecurityDepartments={haidiGroupDeptsBySecurity}
                      config={{
                        name: `hai-${haiId}-OnsetDepartment`,
                        ui: {
                          label: 'hai.miniScreen.hai.onsetDepartment',
                          placeholder: 'hai.miniScreen.hai.onsetDepartment.unknown',
                        },
                      }}
                      multi={false}
                      optional
                      // without an explicit maxWidth the select renders at a FIXED 362px width
                      // (LightSelectFilter default) — the icon offsets below assume the input
                      // fills this relatively-positioned wrapper exactly
                      maxWidth={'100%'}
                      value={onsetDepartmentInput}
                      onChange={(newValue) => {
                        setOnsetDepartmentInput(newValue ?? '');
                      }}
                      disabled={onsetDepartmentLockedInput}
                      isDefaultValue={haiInfoDiff.onsetDepartment === undefined}
                      getOptionDisabled={(option) => {
                        return option.levelType !== 'DEPARTMENT';
                      }}
                    />
                  </TooltipWrapper>
                  {/* null onset organization = "Unknown" — there is no Unknown organization row to
                      select, so the locked empty field renders the translated text as an overlay */}
                  {Boolean(onsetDepartmentLockedInput) && !onsetDepartmentInput && (
                    <Typography
                      data-name={`hai-${haiId}-onsetDepartment-unknown`}
                      variant='basicText'
                      fontSize='14px'
                      css={muiStyles.onsetDepartmentUnknownValue}
                    >
                      {t('hai.miniScreen.hai.onsetDepartment.unknown')}
                    </Typography>
                  )}
                  <DlMuiDynamicIcon
                    data-name={`hai-${haiId}-onsetDepartment-lock`}
                    data-locked={onsetDepartmentLockedInput ? 'true' : 'false'}
                    css={muiStyles.onsetDepartmentLockIcon}
                    iconName={onsetDepartmentLockedInput ? 'lock' : 'lockOpen'}
                    noButtonStyles
                    svgIconInputCss={muiStyles.onsetDepartmentLockIconSvg}
                    iconTitle={
                      onsetDepartmentLockedInput
                        ? 'hai.miniScreen.hai.onsetDepartment.unlock.button'
                        : 'hai.miniScreen.hai.onsetDepartment.lock.button'
                    }
                    tooltipPlacement='top'
                    // lift the tooltip above the field's floating label (the icon sits inside
                    // the field, so plain 'top' would cover the label)
                    tooltipPopperProps={{modifiers: [{name: 'offset', options: {offset: [0, 28]}}]}}
                    onClick={(event) => {
                      setOnsetLockConfirm({
                        anchor: event.currentTarget,
                        action: onsetDepartmentLockedInput ? 'unlock' : 'relock',
                      });
                    }}
                  />
                </div>
              </div>
            </div>
            {typeInput === 'SSI' && (
              <>
                <div css={muiStyles.haiEditRow}>
                  <LightSelectFilter
                    maxWidth={'100%'}
                    config={{name: 'surgeryTypes', ui: {label: 'hai.miniScreen.hai.surgeryTypes'}}}
                    optional
                    options={surgeryTypeOptions}
                    multi
                    freeSolo
                    freeSoloFormat={`{}`}
                    value={surgeryTypesInput}
                    onChange={(newValue) => {
                      setSurgeryTypesInput(newValue);
                      if (newValue == null || (Array.isArray(newValue) && newValue.length === 0)) {
                        setSurgeryDateInput(undefined);
                      }
                    }}
                    isDefaultValue={haiInfoDiff.surgeryTypes === undefined}
                  />
                </div>
                <div css={muiStyles.haiEditRow}>
                  <LightDateFilter
                    style={{maxWidth: '100%'}}
                    config={{
                      name: `hai-${haiId}-SurgeryDate`,
                      ui: {
                        label: 'hai.miniScreen.hai.surgeryDate',
                        placeholder: 'hai.miniScreen.hai.surgeryDate',
                      },
                    }}
                    value={surgeryDateInput}
                    onChange={(newValue) => {
                      setSurgeryDateInput(newValue ?? '');
                    }}
                    disabled={surgeryTypesInput == null || surgeryTypesInput.length === 0}
                    error={errors.surgeryDate}
                    isDefaultValue={haiInfoDiff.surgeryDate === undefined}
                    placement='bottom-start'
                    optional
                    showClearButton
                  />
                </div>
              </>
            )}
            <div css={muiStyles.haiEditRow}>
              <LightSelectFilter
                maxWidth={'100%'}
                config={{name: 'invasiveDevices', ui: {label: 'hai.miniScreen.hai.invasiveDevices'}}}
                optional
                options={invasiveDeviceOptions}
                multi
                freeSolo
                freeSoloFormat={`{}`}
                value={invasiveDevicesInput}
                onChange={(newValue) => {
                  setInvasiveDevicesInput(newValue);
                  if (newValue == null || (Array.isArray(newValue) && newValue.length === 0)) {
                    setInvasiveDeviceDateInput(undefined);
                  }
                }}
                isDefaultValue={haiInfoDiff.invasiveDevices === undefined}
              />
            </div>
            <div css={muiStyles.haiEditRow}>
              <LightDateFilter
                style={{maxWidth: '100%'}}
                config={{
                  name: `hai-${haiId}-InvasiveDeviceDate`,
                  ui: {
                    label: 'hai.miniScreen.hai.invasiveDeviceDate',
                    placeholder: 'hai.miniScreen.hai.invasiveDeviceDate',
                  },
                }}
                value={invasiveDeviceDateInput}
                onChange={(newValue) => {
                  setInvasiveDeviceDateInput(newValue ?? '');
                }}
                disabled={invasiveDevicesInput == null || invasiveDevicesInput.length === 0}
                error={errors.invasiveDeviceDate}
                isDefaultValue={haiInfoDiff.invasiveDeviceDate === undefined}
                placement='bottom-start'
                optional
                showClearButton
              />
            </div>
            <div css={muiStyles.haiEditRow}>
              <LightSelectFilter
                config={{
                  name: `hai-${haiId}-Origin`,
                  ui: {label: 'hai.miniScreen.hai.origin', placeholder: 'hai.miniScreen.hai.origin'},
                }}
                optional
                options={sortByMessageKey((enumMap.Origin || []).filter((v) => !v.isDeprecated))}
                value={originInput}
                onChange={(newValue) => {
                  setOriginInput(newValue);
                }}
                isDefaultValue={haiInfoDiff.origin === undefined}
              />
            </div>
            <div css={muiStyles.haiEditRow}>
              <LightSelectFilter
                config={{
                  name: `hai-${haiId}-Agens`,
                  ui: {label: 'hai.miniScreen.hai.agens'},
                }}
                optional
                options={sortedAgensData ?? []}
                multi
                value={agensInput}
                onChange={(newValue) => {
                  setAgensInput(newValue);
                }}
                isDefaultValue={haiInfoDiff.agens === undefined}
              />
            </div>
            <div css={muiStyles.haiEditRow}>
              <LightSelectFilter
                config={{
                  name: `hai-${haiId}-ReportedBy`,
                  ui: {label: 'hai.miniScreen.hai.reportedBy'},
                }}
                optional
                options={sortByMessageKey(enumMap.Reporter)}
                multi
                value={reportedByInput}
                onChange={(newValue) => {
                  setReportedByInput(newValue);
                }}
                isDefaultValue={haiInfoDiff.reportedBy === undefined}
              />
            </div>
            <div css={muiStyles.haiEditRow}>
              <div
                data-newhai={isNewHai ? 'true' : 'false'}
                data-default={isCheckedByEpiDefaultForExisting ? 'true' : 'false'}
                css={muiStyles.newHaiDefaultSelectFrame}
              >
                <LightSelectFilter
                  config={{
                    name: `hai-${haiId}-CheckedByEpi`,
                    ui: {label: 'hai.miniScreen.hai.checkedByEpi'},
                  }}
                  isClearable={false}
                  options={sortByMessageKey(enumMap.CheckedByEpi)}
                  value={checkedByEpiInput ? CHECKED_BY_EPI_YES.name : CHECKED_BY_EPI_NO.name}
                  onChange={(newValue) => {
                    setCheckedByEpiInput(newValue === CHECKED_BY_EPI_YES.name);
                  }}
                  isDefaultValue={haiInfoDiff.checkedByEpi === CHECKED_BY_EPI_NO.name}
                />
              </div>
            </div>
            {!isNewHai &&
              (!isEditing ? (
                <div css={muiStyles.haiEditRow}>
                  <HaiLabel label='hai.miniScreen.hai.basedOn' muiStyles={muiStyles} />
                  {viewBasedOnChips}
                </div>
              ) : (
                <div css={muiStyles.haiEditRow}>
                  <LightSelectFilter
                    config={{
                      name: `hai-${haiId}-BasedOn`,
                      ui: {label: 'hai.miniScreen.hai.basedOn'},
                    }}
                    placeholderText={''}
                    disabled
                    isClearable={false}
                    options={(hai?.rules ?? []).map((a) => ({id: a, name: a, label: a}))}
                    value={hai?.rules}
                    renderTag={editBasedOnRenderTag}
                    multi
                    autocompleteCss={css`
                      & .MuiAutocomplete-popupIndicator {
                        display: none;
                      }
                      & .MuiOutlinedInput-root {
                        background-color: #f5f5f5;
                      }
                      & .MuiOutlinedInput-root.Mui-disabled {
                        background-color: #f5f5f5;
                      }
                      & .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
                        border-color: rgba(0, 0, 0, 0.23);
                      }
                    `}
                  />
                </div>
              ))}
            <div css={muiStyles.haiEditRow}>
              <LightSelectFilter
                config={{
                  name: `hai-${haiId}-Definition`,
                  ui: {
                    label: 'hai.miniScreen.hai.definition',
                  },
                }}
                optional
                options={sortByMessageKey(enumMap.Definition)}
                multi
                value={definitionInput}
                onChange={(newValue) => {
                  setDefinitionInput(newValue);
                }}
                isDefaultValue={haiInfoDiff.definition === undefined}
              />
            </div>
            <div css={muiStyles.haiEditRow}>
              <div
                data-newhai={isNewHai ? 'true' : 'false'}
                data-default={isDeathDefaultForExisting ? 'true' : 'false'}
                css={muiStyles.newHaiDefaultSelectFrame}
              >
                <LightSelectFilter
                  config={{
                    name: `hai-${haiId}-Death`,
                    ui: {label: 'hai.miniScreen.hai.death', placeholder: 'hai.miniScreen.hai.death'},
                  }}
                  options={sortByMessageKey(enumMap.HaiDeathEnum)}
                  value={deathInput}
                  onChange={(newValue) => {
                    setDeathInput(newValue);
                  }}
                  isDefaultValue={haiInfoDiff.death === DEATH_NO.name}
                  isClearable={false}
                />
              </div>
            </div>
            <div css={muiStyles.haiEditRow}>
              <LightSelectFilter
                maxWidth={'100%'}
                config={{name: 'userTags', ui: {label: 'hai.miniScreen.hai.userTags'}}}
                optional
                options={userTagOptions}
                multi
                freeSolo
                freeSoloFormat={`{}`}
                value={userTagsInput}
                onChange={(newValue) => {
                  setUserTagsInput(newValue);
                }}
                isDefaultValue={haiInfoDiff.userTags === undefined}
              />
            </div>
            <div css={muiStyles.haiEditRow}>
              <LightTextAreaFilter
                style={{maxWidth: '100%'}}
                config={{
                  name: `hai-${haiId}-Comment`,
                  ui: {label: 'hai.miniScreen.hai.comment', placeholder: t('hai.miniScreen.hai.comment')},
                }}
                optional
                value={commentInput}
                onChange={(newValue) => {
                  setCommentInput(newValue ?? '');
                }}
                isDefaultValue={haiInfoDiff.comment === undefined}
              />
            </div>
            <div css={muiStyles.haiEditRow} data-changelog='true'>
              <HaiLabel label='hai.miniScreen.hai.changedBy' muiStyles={muiStyles} />
              <HaiChangelog
                data-name={`hai-${haiId}-changedBy`}
                hai={hai}
                isEditing={false}
                storageIdPrefix={storageIdPrefix}
                muiStyles={muiStyles}
                pathogensData={agensData ?? []}
                locale={locale}
                getTranslationForStandardTypeId={getTranslationForStandardTypeId}
                getInvasiveDeviceLabel={getInvasiveDeviceLabel}
                getCodeLabelForCode={getCodeLabelForCode}
                standardType={standardType}
              />
            </div>
            <div css={muiStyles.haiEditRow} data-vertical>
              {haveErrors && <LightErrorBox message={t('hai.miniScreen.hai.invalidChanges')} />}
              {haveUnsavedChanges && !haveErrors && (
                <div css={muiStyles.unsavedChanges} data-name='unsaved-changes'>
                  <DlMuiDynamicIcon
                    iconName='haidiWarningIcon'
                    useFixedViewBox='0 0 13 12'
                    style={{
                      color: 'inherit',
                      padding: 0,
                      margin: 0,
                      pointerEvents: 'none',
                    }}
                    svgIconInputCss={muiStyles.unsavedChangesIcon}
                    noButtonStyles
                  />
                  <Typography variant='value'>{t('hai.miniScreen.hai.unsavedChanges')}</Typography>
                </div>
              )}
              <div css={muiStyles.editButtons}>
                {canDeleteHai && (
                  <DlMuiButton
                    data-name='hai-remove-button'
                    type='chip-gray'
                    big
                    title='autoLayout.button.remove'
                    leftIcon={
                      <DlMuiDynamicIcon
                        iconName='haidiDeleteIcon'
                        useFixedViewBox={16}
                        style={{
                          color: 'inherit',
                          padding: 0,
                          margin: 0,
                          pointerEvents: 'none',
                        }}
                        svgIconInputCss={muiStyles.deleteIcon}
                        noButtonStyles
                      />
                    }
                    onClick={() => {
                      setConfirmDeleteOpened(true);
                    }}
                  />
                )}
                <DlMuiButton
                  data-name='hai-reset-button'
                  type='chip-gray'
                  big
                  // title='autoLayout.button.reset'
                  title='common.confirmDialog.cancel'
                  onClick={() => {
                    if (!haveUnsavedChanges) {
                      resetAndClose();
                    } else {
                      setConfirmResetOpened(true);
                    }
                  }}
                />
              </div>
              <div css={muiStyles.editButtons}>
                <DlMuiButton
                  data-name='hai-save-button'
                  type='chip2-filled'
                  big
                  title='autoLayout.button.saveChanges'
                  onClick={() => {
                    if (validate()) {
                      if (isSaving) return;
                      setIsSaving(true);
                      if (isNewHai) {
                        mutateCreateHai();
                      } else {
                        mutateEditHai();
                      }
                    }
                  }}
                  disabled={!userRoles.HAI_EDIT_ROLE || !haveUnsavedChanges}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {isMaximized && (
              <div css={muiStyles.haiRow}>
                <HaiLabel label={`hai.miniScreen.hai.standardType.${standardType}`} muiStyles={muiStyles} />
                <HaiValue
                  data-name={`hai-${haiId}-StandardType`}
                  value={haiInfo.standardTypeId ? getCodeLabelForStandardTypeId(haiInfo.standardTypeId as any) : EMPTY}
                  muiStyles={muiStyles}
                />
              </div>
            )}
            {haiInfo.type === 'BSI' && (
              <div css={muiStyles.haiRow}>
                <HaiLabel label='hai.miniScreen.hai.bsiOrigin' muiStyles={muiStyles} />
                <HaiValue
                  data-name={`hai-${haiId}-bsiOrigin`}
                  value={translations.bsiOriginTranslation(haiInfo.bsiOrigin as any)}
                  muiStyles={muiStyles}
                />
              </div>
            )}
            {isMaximized && (
              <div css={muiStyles.haiRow}>
                <HaiLabel label='hai.miniScreen.hai.state' muiStyles={muiStyles} />
                <HaiValue
                  data-name={`hai-${haiId}-state`}
                  value={translations.stateTranslation(haiInfo.state as any)}
                  muiStyles={muiStyles}
                />
              </div>
            )}
            <div css={muiStyles.haiRow}>
              <HaiLabel label='hai.miniScreen.hai.department' tooltip={t('hai.miniScreen.hai.department.tooltip')} muiStyles={muiStyles} />
              <HaiValue data-name={`hai-${haiId}-department`} value={hai?.organizationLongLabel ?? EMPTY} muiStyles={muiStyles} />
            </div>
            {/* IDEA-290: no separate read-only row for the onset department — it renders inline
                after the onset date, and only when it differs from the source department. */}
            <div css={muiStyles.haiRow} data-last={!isMaximized}>
              <HaiLabel label='hai.miniScreen.hai.date' tooltip={t('hai.miniScreen.hai.date.tooltip')} muiStyles={muiStyles} />
              <div css={muiStyles.haiValueWithNote}>
                <HaiValue data-name={`hai-${haiId}-date`} value={formatDate(haiInfo.date)} muiStyles={muiStyles} />
                {onsetDepartmentNote && (
                  <TooltipWrapper
                    tooltip={t('hai.miniScreen.hai.onsetDepartment.tooltip')}
                    noPadding
                    wrapperStyle={{background: 'transparent', padding: 0}}
                  >
                    <Typography
                      data-name={`hai-${haiId}-onsetDepartmentNote`}
                      variant='basicText'
                      fontSize='14px'
                      css={muiStyles.onsetDepartmentNote}
                    >
                      {onsetDepartmentNote}
                    </Typography>
                  </TooltipWrapper>
                )}
              </div>
            </div>
          </>
        )
      }
    >
      {isEditing ? (
        <></>
      ) : (
        <>
          {haiInfo.type !== 'BSI' && <div css={muiStyles.haiRow} style={{display: 'none'}} />}
          {haiInfo.type === 'SSI' && (
            <>
              <div css={muiStyles.haiRow}>
                <HaiLabel label='hai.miniScreen.hai.surgeryTypes' muiStyles={muiStyles} />
                {haiInfo.surgeryTypes.length > 0 ? (
                  getTextChips(haiInfo.surgeryTypes, muiStyles.haiChip, `hai-${haiId}-surgeryTypes`, muiStyles.haiState)
                ) : (
                  <HaiValue data-name={`hai-${haiId}-surgeryTypes`} value={EMPTY} muiStyles={muiStyles} />
                )}
              </div>
              <div css={muiStyles.haiRow}>
                <HaiLabel label='hai.miniScreen.hai.surgeryDate' muiStyles={muiStyles} />
                <HaiValue
                  data-name={`hai-${haiId}-surgeryDate`}
                  value={haiInfo.surgeryDate ? formatDate(haiInfo.surgeryDate) : EMPTY}
                  muiStyles={muiStyles}
                />
              </div>
            </>
          )}
          <div css={muiStyles.haiRow}>
            <HaiLabel label='hai.miniScreen.hai.invasiveDevices' muiStyles={muiStyles} />
            {haiInfo.invasiveDevices.length > 0 ? (
              getTextChips(
                haiInfo.invasiveDevices.map(getInvasiveDeviceLabel),
                muiStyles.haiChip,
                `hai-${haiId}-invasiveDevices`,
                muiStyles.haiState
              )
            ) : (
              <HaiValue data-name={`hai-${haiId}-invasiveDevices`} value={EMPTY} muiStyles={muiStyles} />
            )}
          </div>
          <div css={muiStyles.haiRow}>
            <HaiLabel label='hai.miniScreen.hai.invasiveDeviceDate' muiStyles={muiStyles} />
            <HaiValue
              data-name={`hai-${haiId}-invasiveDeviceDate`}
              value={haiInfo.invasiveDeviceDate ? formatDate(haiInfo.invasiveDeviceDate) : EMPTY}
              muiStyles={muiStyles}
            />
          </div>
          <div css={muiStyles.haiRow}>
            <HaiLabel label='hai.miniScreen.hai.origin' muiStyles={muiStyles} />
            <HaiValue
              data-name={`hai-${haiId}-origin`}
              value={translations.originTranslation(haiInfo.origin as any)}
              muiStyles={muiStyles}
            />
          </div>
          <div css={muiStyles.haiRow}>
            <HaiLabel label='hai.miniScreen.hai.agens' muiStyles={muiStyles} />
            <HaiValue
              data-name={`hai-${haiId}-agens`}
              value={translations.pathogensTranslation(sortPathogensByLocale(hai?.pathogens ?? [], locale))}
              muiStyles={muiStyles}
            />
          </div>
          <div css={muiStyles.haiRow}>
            <HaiLabel label='hai.miniScreen.hai.reportedBy' muiStyles={muiStyles} />
            {haiInfo.reportedBy != null && haiInfo.reportedBy.length > 0 ? (
              getTextChips(
                translations.reporterTranslation(haiInfo.reportedBy as any),
                muiStyles.haiChip,
                `hai-${haiId}-reportedBy`,
                muiStyles.haiState
              )
            ) : (
              <HaiValue data-name={`hai-${haiId}-reportedBy`} value={EMPTY} muiStyles={muiStyles} />
            )}
          </div>
          <div css={muiStyles.haiRow}>
            <HaiLabel label='hai.miniScreen.hai.checkedByEpi' muiStyles={muiStyles} />
            <HaiValue
              data-name={`hai-${haiId}-checkedByEpi`}
              value={t(haiInfo.checkedByEpi ? 'hai.miniScreen.option.value.yes' : 'hai.miniScreen.option.value.no')}
              muiStyles={muiStyles}
            />
          </div>
          <div css={muiStyles.haiRow}>
            <HaiLabel label='hai.miniScreen.hai.basedOn' muiStyles={muiStyles} />
            {hai?.rules != null && hai.rules.length > 0 ? (
              getTextChips(
                (hai?.rules ?? []).map((a) => ({
                  text: translations.ruleTranslation(a),
                  tooltip: translations.ruleTooltipsTranslation(a, hai?.ssiPeriodDays),
                })),
                muiStyles.haiChip,
                `hai-${haiId}-basedOn`,
                muiStyles.haiState
              )
            ) : (
              <HaiValue data-name={`hai-${haiId}-basedOn`} value={EMPTY} muiStyles={muiStyles} />
            )}
          </div>
          <div css={muiStyles.haiRow}>
            <HaiLabel label='hai.miniScreen.hai.definition' muiStyles={muiStyles} />
            {haiInfo.definition != null && haiInfo.definition.length > 0 ? (
              getTextChips(
                translations.definitionsTranslation(haiInfo.definition as any),
                muiStyles.haiChip,
                `hai-${haiId}-definition`,
                muiStyles.haiState
              )
            ) : (
              <HaiValue data-name={`hai-${haiId}-definition`} value={EMPTY} muiStyles={muiStyles} />
            )}
          </div>
          <div css={muiStyles.haiRow}>
            <HaiLabel label='hai.miniScreen.hai.death' muiStyles={muiStyles} />
            <HaiValue data-name={`hai-${haiId}-death`} value={translations.deathTranslation(haiInfo.death as any)} muiStyles={muiStyles} />
          </div>
          <div css={muiStyles.haiRow}>
            <HaiLabel label='hai.miniScreen.hai.userTags' muiStyles={muiStyles} />
            {haiInfo.userTags.length > 0 ? (
              getTextChips(haiInfo.userTags, muiStyles.haiChip, `hai-${haiId}-userTags`, muiStyles.haiState)
            ) : (
              <HaiValue data-name={`hai-${haiId}-userTags`} value={EMPTY} muiStyles={muiStyles} />
            )}
          </div>
          <div css={muiStyles.haiRow}>
            <HaiLabel label='hai.miniScreen.hai.comment' muiStyles={muiStyles} />
            <HaiValue data-name={`hai-${haiId}-comment`} value={haiInfo.comment} muiStyles={muiStyles} />
          </div>
          <div css={muiStyles.haiRow} data-last>
            <HaiLabel label='hai.miniScreen.hai.changedBy' muiStyles={muiStyles} />
            <HaiChangelog
              data-name={`hai-${haiId}-changedBy`}
              hai={hai}
              isEditing={isEditing}
              storageIdPrefix={storageIdPrefix}
              muiStyles={muiStyles}
              pathogensData={agensData ?? []}
              locale={locale}
              getTranslationForStandardTypeId={getTranslationForStandardTypeId}
              getInvasiveDeviceLabel={getInvasiveDeviceLabel}
              getCodeLabelForCode={getCodeLabelForCode}
              standardType={standardType}
            />
          </div>
        </>
      )}
      <ConfirmDialog
        isOpened={confirmDeleteOpened}
        handleConfirm={onConfirmDelete}
        handleCancel={onCancelDelete}
        titleIcon={
          <DlMuiDynamicIcon
            iconName='haidiWarningIcon'
            useFixedViewBox='0 0 13 12'
            css={muiStyles.confirmIconButton}
            svgIconInputCss={muiStyles.confirmIcon}
            noButtonStyles
          />
        }
      >
        <Typography variant='value'>{t('hai.miniScreen.hai.delete.message')}</Typography>
      </ConfirmDialog>
      <ConfirmDialog
        isOpened={confirmResetOpened}
        handleConfirm={onConfirmReset}
        handleCancel={onCancelReset}
        titleIcon={
          <DlMuiDynamicIcon
            iconName='haidiWarningIcon'
            useFixedViewBox='0 0 13 12'
            css={muiStyles.confirmIconButton}
            svgIconInputCss={muiStyles.confirmIcon}
            noButtonStyles
          />
        }
      >
        <Typography variant='value'>{t('hai.miniScreen.hai.reset.message')}</Typography>
        <div css={muiStyles.yourChanges} data-name='your-changes'>
          <Typography variant='value' css={muiStyles.yourChangesLabel}>
            {t('hai.miniScreen.hai.reset.yourChanges')}
          </Typography>
          {Object.entries(haiInfoDiff).map(([k, v]) => {
            // console.log('entry', k, v);
            return (
              <div key={k} css={muiStyles.yourChangesRow}>
                <Typography variant='label' css={muiStyles.yourChangesRowLabel}>
                  {k === 'standardTypeId' ? t(`hai.miniScreen.hai.standardType.${standardType}`) : t(`hai.miniScreen.hai.${k}`)}
                </Typography>
                <Typography variant='value' css={muiStyles.yourChangesRowValue}>
                  {v}
                </Typography>
              </div>
            );
          })}
        </div>
      </ConfirmDialog>
      <ConfirmDialog
        isOpened={confirmInconsistentTypeOpened.opened}
        handleConfirm={onConfirmInconsistentType}
        handleCancel={onCancelInconsistentType}
        titleIcon={
          <DlMuiDynamicIcon
            iconName='haidiWarningIcon'
            useFixedViewBox='0 0 13 12'
            css={muiStyles.confirmIconButton}
            svgIconInputCss={muiStyles.confirmIcon}
            noButtonStyles
          />
        }
      >
        <Typography variant='value'>{t(`hai.miniScreen.hai.type.${standardType}.inconsistent.message`)}</Typography>
      </ConfirmDialog>
      <PopupWrapper
        openButton={false}
        anchorTag={onsetLockConfirm?.anchor}
        direction='up'
        yOffset={4}
        showDiscardSaveButtons
        cancelTitle='common.confirmDialog.cancel'
        confirmTitle='common.confirmDialog.confirm'
        // chip2-filled = the dark filled button of the modal ConfirmDialog; haidi styles the default
        // chip-filled as a pale secondary (--dlChipButtonFilledBackground: --dlTheme-100)
        confirmButtonType='chip2-filled'
        onClose={onCancelOnsetLockToggle}
        onConfirm={onConfirmOnsetLockToggle}
        contentStyle={{maxWidth: 420}}
        data-name={`hai-${haiId}-onsetDepartment-lock-confirm`}
      >
        <Typography variant='sectionHeader'>
          {t(
            onsetLockConfirm?.action === 'relock'
              ? 'hai.miniScreen.hai.onsetDepartment.relock.message'
              : 'hai.miniScreen.hai.onsetDepartment.unlock.message'
          )}
        </Typography>
      </PopupWrapper>
    </LightAccordion>
  );
};
export default EncounterHaiItem;
const getStyles: GetStylesFunction = (options) => ({
  accordionHeaderLeft: css`
    flex-grow: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
  `,
  accordionHeaderTypeText: css`
    width: 50%;
  `,
  accordionHeaderLeftRight: css`
    width: 50%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
  `,
  haiState: css`
    overflow: hidden;
    background: white;
    border-radius: 12px;
    padding: 6px 8px;
    display: inline-flex;
    gap: 6px;
  `,
  haiStateIcon: css`
    width: 12px;
    height: 12px;
  `,
  haiStateText: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  haiEditIcon: css`
    width: 12px;
    height: 12px;
  `,
  haiRow: css`
    min-height: 40px;
    display: flex;
    padding: 0 8px;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    &:nth-child(2n + 1) {
      background: var(--dlThemeGray-100);
    }
    &[data-last='true'] {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  `,
  newHaiDefaultSelectFrame: css`
    display: flex;
    > * {
      flex: 1 1 auto;
      min-width: 0;
    }
    &[data-newhai='false'][data-default='true'] {
      & .MuiFormControl-root[data-occupied='true'] fieldset {
        border: 1px solid var(--dlThemeGray-400) !important;
      }
      & .MuiFormControl-root:focus-within[data-occupied='true'] fieldset {
        border: 2px solid var(--dlMuiTextFieldBorderColorFocus) !important;
      }
      & .MuiOutlinedInput-root:not(.Mui-focused) .MuiOutlinedInput-notchedOutline {
        border-color: var(--dlThemeGray-400) !important;
      }
    }
  `,
  haiEditRow: css`
    min-height: 40px;
    display: flex;
    padding: 0 8px;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    &[data-vertical='true'] {
      padding-top: 6px;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 8px;
    }
    &:not([data-vertical='true']) > *:first-of-type {
      width: calc(38% - 16px);
    }
    &:not([data-vertical='true']) > *:last-of-type {
      width: calc(62%);
    }
    &:not([data-vertical='true']) > *:only-child {
      width: 100%;
    }
    &[data-changelog='true'] > *:first-of-type {
      width: auto;
    }
    &[data-changelog='true'] > *:last-of-type {
      width: 80%;
    }
    .MuiInputBase-input,
    input,
    textarea,
    select {
      color: var(--dlAgGridFontColor) !important;
    }

    .MuiFormControl-root .MuiOutlinedInput-root {
      color: var(--dlAgGridFontColor) !important;
    }

    .MuiFormControl-root[data-error='true'] .MuiOutlinedInput-root {
      color: #d32f2f !important;
    }

    .MuiFormControl-root label,
    .MuiInputLabel-root,
    .MuiFormLabel-root {
      color: var(--dlAgGridFontColor) !important;
    }

    .MuiFormControl-root[data-error='true'] label {
      color: #d32f2f !important;
    }

    .MuiInputLabel-root.Mui-focused {
      color: var(--dlAgGridFontColor) !important;
    }

    .MuiInputLabel-root:not(.Mui-error) {
      color: var(--dlAgGridFontColor) !important;
    }
  `,
  onsetDateDepartmentRow: css`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  onsetDepartmentField: css`
    position: relative;
    flex: 1 1 62%;
    min-width: 0;
    /* locked (IDEA-290): filled (same fill as the read-only "based on" field), borderless,
       8px-rounded field with the lock icon in place of the (hidden) dropdown arrow */
    &[data-locked='true'] .MuiAutocomplete-endAdornment {
      display: none;
    }
    &[data-locked='true'] .MuiOutlinedInput-root {
      background: #f5f5f5;
      border-radius: 8px;
      padding-right: 30px !important;
    }
    /* same outline as the disabled "based on" (Důvod hlášení) field — its RENDERED border is
       --dlThemeGray-300 (#E2E8F2), not the rgba() its own css declares (measured live) */
    &[data-locked='true'] .MuiOutlinedInput-notchedOutline {
      border-color: var(--dlThemeGray-300) !important;
    }
    /* unlocked: regular editable field — icon order is [clear] [lock] [dropdown arrow]: the
       clear indicator gets a margin that opens a gap for the absolutely positioned lock icon,
       and the input padding keeps the value text clear of all three icons */
    &[data-locked='false'] .MuiOutlinedInput-root {
      padding-right: 86px !important;
    }
    &[data-locked='false'] .MuiAutocomplete-clearIndicator {
      margin-right: 28px;
    }
    /* the un-shrunk (floating) label is absolutely positioned and ignores the input padding —
       cap its width so it never runs under the icons */
    &[data-locked='true'] .MuiInputLabel-root:not(.MuiInputLabel-shrink) {
      max-width: calc(100% - 44px);
    }
    &[data-locked='false'] .MuiInputLabel-root:not(.MuiInputLabel-shrink) {
      max-width: calc(100% - 90px);
    }
    /* locked + empty (= unknown onset department, data-unknown mirrors the overlay condition):
       float the label up so the "Unknown" overlay below it stays readable (a disabled field can
       never be focused, so MUI would keep the label down over the overlay; data-occupied can't
       be used — an empty value differing from the stored one still counts as occupied) */
    &[data-unknown='true'] .MuiInputLabel-root:not(.MuiInputLabel-shrink) {
      transform: translate(14px, -9px) scale(0.75) !important;
    }
  `,
  onsetDepartmentUnknownValue: css`
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--dlThemeGray-700);
    font-style: italic;
    pointer-events: none;
    max-width: calc(100% - 44px);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  `,
  onsetDepartmentLockIcon: css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    padding: 2px;
    color: var(--dlThemeGray-700);
    &[data-locked='true'] {
      right: 6px;
    }
    &[data-locked='false'] {
      /* centered in the gap the clear indicator's 28px margin opens between the clear and
         dropdown buttons — measured live: popup spans 7..27px from the input's right edge,
         so the 20px lock at right:31 sits with a symmetric 4px gap on both sides */
      right: 31px;
    }
  `,
  onsetDepartmentLockIconSvg: css`
    width: 16px;
    height: 16px;
  `,
  haiValueWithNote: css`
    min-width: 0;
    display: flex;
    justify-content: flex-end;
    align-items: baseline;
    gap: 4px;
    flex-wrap: wrap;
  `,
  onsetDepartmentNote: css`
    padding: 9px 0;
    color: var(--dlThemeGray-500);
    font-style: italic;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  haiLabel: css`
    padding: 11px 0;
    color: var(--dlTheme-700);
  `,
  haiValue: css`
    padding: 9px 0;
    color: var(--dlTheme-700);
    white-space: pre-line;
  `,
  haiChip: css`
    background: var(--dlThemeGray-300);
    color: var(--dlTheme-700);
    border-radius: 12px;
    padding: 0px 8px;
    margin: 2px 0px;
    font-size: 14px;
    line-height: 24px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  `,
  unsavedChanges: css`
    width: 100%;
    min-height: 26px;
    background: var(--dlTheme-100);
    color: var(--dlTheme-500);
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
  `,
  unsavedChangesIcon: css`
    font-size: 16px;
  `,
  editButtons: css`
    width: 100%;
    padding: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    & > div {
      display: flex;
      flex: 1;
    }
    & > div > div.MuiButtonBase-root {
      flex: 1;
    }
  `,
  deleteIcon: css`
    font-size: 16px;
  `,
  resetIcon: css`
    font-size: 12px;
  `,
  confirmIconButton: css`
    color: var(--dlTheme-500);
    padding: 0;
  `,
  confirmIcon: css`
    font-size: 20px;
  `,
  yourChanges: css`
    border-radius: 8px;
    padding: 16px;
    background: var(--dlThemeGray-100);
    color: var(--dlThemeGray-500);
  `,
  yourChangesLabel: css`
    margin-bottom: 16px;
  `,
  yourChangesRow: css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 16px;
  `,
  yourChangesRowLabel: css`
    color: var(--dlThemeGray-600);
  `,
  yourChangesRowValue: css``,
  changelogWrapper: css`
    width: 80%;
    display: block;
    padding: 8px 0;
  `,
  changelog: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  changelogItem: css`
    background: #fafbfd;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  `,
  changelogItemHeader: css`
    background: var(--dlThemeGray-300);
    cursor: pointer;
    &:hover,
    &:active {
      background: var(--dlThemeGray-400);
    }
    & * {
      user-select: none;
    }
  `,
  changelogItemHeaderLeft: css`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--dlTheme-700);
    overflow: hidden;
  `,
  changelogItemHeaderText: css``,
  changelogItemHeaderTextShrink: css`
    flex-shrink: 1;
  `,
  changelogItemHeaderSpacer: css`
    width: 1px;
    height: 16px;
    background: var(--dlTheme-700);
  `,
  changelogItemHeaderIconButton: css`
    padding: 0;
    width: 16px;
    height: 16px;
  `,
  changelogItemHeaderIcon: css`
    width: 16px;
    height: 16px;
    cursor: pointer;
  `,
  changelogItemRows: css`
    display: flex;
    flex-direction: column;
    padding: 8px;
    gap: 8px;
  `,
  changelogItemRowGroup: css`
    display: flex;
    flex-direction: column;
  `,
  changelogItemRowGroupHeader: css`
    color: var(--dlThemeGray-500);
    font-family: var(--dlFontFamilySemiBold);
    line-height: 16px;
  `,
  changelogItemRow: css`
    display: flex;
    color: var(--dlThemeGray-500);
    line-height: 16px;
    padding: 4px 8px;
    white-space: pre-wrap;
  `,
  changelogItemHighlightKey: css`
    display: inline-block;
    width: 50%;
    padding-right: 4px;
    color: var(--dlThemeGray-700);
    font-family: var(--dlFontFamilySemiBold);
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  changelogItemRight: css`
    display: inline-flex;
    width: 50%;
    align-items: stretch;
  `,
  changelogItemJoiner: css`
    color: var(--dlThemeGray-500);
    font-family: var(--dlFontFamilySemiBold);
    &[data-visible='false'] {
      visibility: hidden;
    }
  `,
  changelogItemHighlightValue: css`
    flex: 1;
    color: var(--dlThemeGray-700);
    font-family: var(--dlFontFamilySemiBold);
  `,
  changelogHistoryButton: css`
    border-radius: 8px;
    overflow: hidden;
    justify-content: center;
    background: var(--dlTheme-100);
    color: var(--dlTheme-500);
    cursor: pointer;
    &:hover,
    &:active {
      background: var(--dlTheme-300);
    }
    & * {
      user-select: none;
    }
  `,
});

type TextAndTooltip = {
  text: string;
  tooltip: string;
};

function getTextChips(inputText: string[] | TextAndTooltip[], css: SerializedStyles, dataName: string, tooltipStyle: SerializedStyles) {
  return (
    <div
      data-name={dataName}
      style={{
        minWidth: 0,
        flex: 1,
        padding: '9px 0',
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'inline-flex',
          flexWrap: 'wrap',
          gap: 8,
          justifyContent: 'flex-end',
        }}
      >
        {inputText.map((v, i) => renderTextTooltipChip(i, v, css, tooltipStyle))}
      </div>
    </div>
  );
}
function renderTextTooltipChip(key: any, v: string | TextAndTooltip, css: SerializedStyles, tooltipStyle: SerializedStyles) {
  return (
    <TooltipWrapper
      key={key}
      tooltip={(v as TextAndTooltip)?.tooltip ?? undefined}
      noPadding
      style={{
        flex: '0 0 auto',
        maxWidth: 'calc(100% - 32px)',
        display: 'flex',
        justifyContent: 'flex-end',
      }}
      wrapperCss={tooltipStyle}
      wrapperStyle={{background: 'transparent', padding: '0px'}}
      data-name='hai-state-header'
    >
      <Typography variant='basicText' fontSize='14px' css={css}>
        {(v as TextAndTooltip)?.text ?? v}
      </Typography>
    </TooltipWrapper>
  );
}
type HaiLabelProps = {
  label: string;
  muiStyles: any;
  tooltip?: string;
};
const HaiLabel: React.FC<HaiLabelProps> = ({label, muiStyles, tooltip, ...extraProps}) => {
  const intl = useIntl();
  const content = (
    <Typography
      {...extraProps}
      variant='basicTextBold'
      fontSize='14px'
      css={muiStyles.haiLabel}
      // Inside the TooltipWrapper the label is no longer a flex item of the row (which would
      // blockify it), so it stays inline and its vertical padding stops contributing to layout
      // — force block display to keep the row vertically aligned like the unwrapped labels.
      style={tooltip ? {display: 'block'} : undefined}
    >
      {TranslateUtils.translate(label, intl)}
    </Typography>
  );
  if (!tooltip) return content;
  return (
    <TooltipWrapper tooltip={tooltip} noPadding wrapperStyle={{background: 'transparent', padding: 0}}>
      {content}
    </TooltipWrapper>
  );
};
type HaiValueProps = {
  value: string | undefined;
  muiStyles: any;
};
const HaiValue: React.FC<HaiValueProps> = ({value, muiStyles, ...extraProps}) => {
  return (
    <Typography {...extraProps} variant='basicText' fontSize='14px' css={muiStyles.haiValue} sx={{whiteSpace: 'pre-line'}}>
      {value || EMPTY}
    </Typography>
  );
};
// changelog
type ParsedChangeRowString = {
  type: 'string' | 'date';
  key: string;
  value: string;
};
type ParsedChangeRowEnum = {
  type: 'enum';
  enumName: string;
  key: string;
  value: string;
};
type ParsedChangeRowEnumArray = {
  type: 'enumArray';
  enumName: string;
  key: string;
  value: string[];
};
type ParsedChangeRowAgensArray = {
  type: 'agensArray';
  key: string;
  value: number[];
};

type ParsedChangeRowStringArray = {
  type: 'stringArray';
  key: string;
  value: string[];
};
type ParsedChangeRowStandardTypeId = {
  type: 'standardTypeId';
  key: string;
  value: number;
};

type ParsedChangeRowStandardType = {
  type: 'standardType';
  key: string;
  value: string;
};

type ParsedChangeRow =
  | ParsedChangeRowString
  | ParsedChangeRowEnum
  | ParsedChangeRowEnumArray
  | ParsedChangeRowAgensArray
  | ParsedChangeRowStringArray
  | ParsedChangeRowStandardTypeId
  | ParsedChangeRowStandardType;

type ParsedChange = {
  _id: number;
  user: string;
  timestamp: string;
  addRows: ParsedChangeRow[];
  editRows: ParsedChangeRow[];
  deleteRows: ParsedChangeRow[];
};

type DiffState = {
  reportedBy: HaidiSchema.components['schemas']['Reporter'][];
  agens: HaiPathogenDto['code'][];
  definition: HaidiSchema.components['schemas']['Definition'][];
};

function parseChanges(
  hai: HaidiSchema.components['schemas']['HAIDetailDto'] | undefined,
  showHistory: boolean,
  getTranslationForStandardTypeId: (id: number) => string,
  getInvasiveDeviceLabel: (value: string) => string
): ParsedChange[] {
  const diffState: DiffState = {
    reportedBy: hai?.reporters ?? [],
    agens: (hai?.pathogens ?? []).map((v) => v.code),
    definition: hai?.definitions ?? [],
  };

  function applyBackDiff<T>(array: T[], toAdd: T[], toDelete: T[]): T[] {
    return [...array, ...toDelete].filter((v) => !toAdd.includes(v));
  }

  const reversedChangeLog = ArrayUtils.reversed(hai?.changeLog ?? []);

  const mapItem = (v: HaidiSchema.components['schemas']['HaiChangelogEntry'], i: number) => {
    const changedFields = (v.changedFields ?? {}) as Record<string, any>;
    const removedFields = (v.removedFields ?? {}) as Record<string, any>;
    const accAddRows = [] as ParsedChangeRow[];
    const accEditRows = [] as ParsedChangeRow[];
    const accDeleteRows = [] as ParsedChangeRow[];

    accEditRows.push({
      type: 'enum',
      enumName: 'HaiTypeEnum',
      key: 'type',
      value: changedFields.type,
    });

    if (changedFields.standardTypeId !== undefined && changedFields.standardTypeId !== null) {
      accEditRows.push({
        type: 'standardTypeId',
        key: 'standardTypeId',
        value: changedFields.standardTypeId,
      });
    }

    if (changedFields.standardType !== undefined && changedFields.standardType !== null) {
      accEditRows.push({
        type: 'standardType',
        key: 'standardType',
        value: changedFields.standardType,
      });
    }

    if (removedFields.standardType !== undefined && removedFields.standardType !== null) {
      accDeleteRows.push({
        type: 'standardType',
        key: 'standardType',
        value: removedFields.standardType,
      });
    }

    accEditRows.push({
      type: 'enum',
      enumName: 'BSIOrigin',
      key: 'bsiOrigin',
      value: changedFields.bsiOrigin,
    });

    if (removedFields.bsiOrigin !== undefined && removedFields.bsiOrigin !== null) {
      accDeleteRows.push({
        type: 'enum',
        enumName: 'BSIOrigin',
        key: 'bsiOrigin',
        value: removedFields.bsiOrigin,
      });
    }

    accEditRows.push({
      type: 'enum',
      enumName: 'PNOrigin',
      key: 'pnOrigin',
      value: changedFields.pnOrigin,
    });

    if (removedFields.pnOrigin !== undefined && removedFields.pnOrigin !== null) {
      accDeleteRows.push({
        type: 'enum',
        enumName: 'PNOrigin',
        key: 'pnOrigin',
        value: removedFields.pnOrigin,
      });
    }

    accEditRows.push({
      type: 'string',
      key: 'department',
      value: changedFields.organization,
    });

    // PDH-3175: BE changelog carries the onset organization as a display label (like `organization`);
    // a removed value means the onset department became unknown.
    accEditRows.push({
      type: 'string',
      key: 'onsetDepartment',
      value: changedFields.onsetOrganization,
    });

    if (removedFields.onsetOrganization !== undefined && removedFields.onsetOrganization !== null) {
      accDeleteRows.push({
        type: 'string',
        key: 'onsetDepartment',
        value: removedFields.onsetOrganization,
      });
    }

    if (changedFields.onsetOrganizationLocked !== undefined && changedFields.onsetOrganizationLocked !== null) {
      accEditRows.push({
        // CheckedByEpi is reused purely for its YES/NO display translation.
        type: 'enum',
        enumName: 'CheckedByEpi',
        key: 'onsetDepartmentLocked',
        value: changedFields.onsetOrganizationLocked ? 'YES' : 'NO',
      });
    }

    accEditRows.push({
      type: 'date',
      key: 'date',
      value: changedFields.date,
    });

    // Merged invasive-device value list: translate predefined enum names, pass free text through.
    if (v.addedArrayEntries.invasiveDevices) {
      accAddRows.push({
        type: 'stringArray',
        key: 'invasiveDevices',
        value: (v.addedArrayEntries.invasiveDevices as unknown as string[]).map(getInvasiveDeviceLabel),
      });
    }
    if (v.removedArrayEntries.invasiveDevices) {
      accDeleteRows.push({
        type: 'stringArray',
        key: 'invasiveDevices',
        value: (v.removedArrayEntries.invasiveDevices as unknown as string[]).map(getInvasiveDeviceLabel),
      });
    }

    if (changedFields.invasiveDeviceDate !== undefined && changedFields.invasiveDeviceDate !== null) {
      accEditRows.push({
        type: 'date',
        key: 'invasiveDeviceDate',
        value: changedFields.invasiveDeviceDate,
      });
    }

    if (removedFields.invasiveDeviceDate !== undefined && removedFields.invasiveDeviceDate !== null) {
      accDeleteRows.push({
        type: 'string',
        key: 'invasiveDeviceDate',
        value: '',
      });
    }

    // Surgery types are a raw-text value list (rendered as plain strings, no enum translation).
    if (v.addedArrayEntries.surgeryTypes) {
      accAddRows.push({
        type: 'stringArray',
        key: 'surgeryTypes',
        value: v.addedArrayEntries.surgeryTypes as unknown as string[],
      });
    }
    if (v.removedArrayEntries.surgeryTypes) {
      accDeleteRows.push({
        type: 'stringArray',
        key: 'surgeryTypes',
        value: v.removedArrayEntries.surgeryTypes as unknown as string[],
      });
    }

    if (changedFields.surgeryDate !== undefined && changedFields.surgeryDate !== null) {
      accEditRows.push({
        type: 'date',
        key: 'surgeryDate',
        value: changedFields.surgeryDate,
      });
    }

    if (removedFields.surgeryDate !== undefined && removedFields.surgeryDate !== null) {
      accDeleteRows.push({
        type: 'string',
        key: 'surgeryDate',
        value: '',
      });
    }

    // User-defined tags are a raw-text value list (rendered as plain strings, no enum translation).
    if (v.addedArrayEntries.userTags) {
      accAddRows.push({
        type: 'stringArray',
        key: 'userTags',
        value: v.addedArrayEntries.userTags as unknown as string[],
      });
    }
    if (v.removedArrayEntries.userTags) {
      accDeleteRows.push({
        type: 'stringArray',
        key: 'userTags',
        value: v.removedArrayEntries.userTags as unknown as string[],
      });
    }

    accEditRows.push({
      type: 'enum',
      enumName: 'Origin',
      key: 'origin',
      value: changedFields.origin,
    });

    if (v.addedArrayEntries.pathogens) {
      accAddRows.push({
        type: 'agensArray',
        key: 'agens',
        value: v.addedArrayEntries.pathogens as unknown as number[],
      });
    }
    if (v.removedArrayEntries.pathogens) {
      accDeleteRows.push({
        type: 'agensArray',
        key: 'agens',
        value: v.removedArrayEntries.pathogens as unknown as number[],
      });
    }
    diffState.agens = applyBackDiff(
      diffState.agens,
      (v.addedArrayEntries.pathogens ?? []) as any,
      (v.removedArrayEntries.pathogens ?? []) as any
    );

    accEditRows.push({
      type: 'enum',
      enumName: 'State',
      key: 'state',
      value: changedFields.state,
    });

    if (v.addedArrayEntries.reporters) {
      accAddRows.push({
        type: 'enumArray',
        enumName: 'Reporter',
        key: 'reportedBy',
        value: v.addedArrayEntries.reporters as unknown as string[],
      });
    }
    if (v.removedArrayEntries.reporters) {
      accDeleteRows.push({
        type: 'enumArray',
        enumName: 'Reporter',
        key: 'reportedBy',
        value: v.removedArrayEntries.reporters as unknown as string[],
      });
    }
    diffState.reportedBy = applyBackDiff(
      diffState.reportedBy,
      (v.addedArrayEntries.reporters ?? []) as any,
      (v.removedArrayEntries.reporters ?? []) as any
    );

    if (changedFields.checkedByEpi !== undefined && changedFields.checkedByEpi !== null) {
      accEditRows.push({
        type: 'enum',
        enumName: 'CheckedByEpi',
        key: 'checkedByEpi',
        value: changedFields.checkedByEpi ? 'YES' : 'NO',
      });
    }

    if (v.addedArrayEntries.definitions) {
      accAddRows.push({
        type: 'enumArray',
        enumName: 'Definition',
        key: 'definition',
        value: v.addedArrayEntries.definitions as unknown as string[],
      });
    }
    if (v.removedArrayEntries.definitions) {
      accDeleteRows.push({
        type: 'enumArray',
        enumName: 'Definition',
        key: 'definition',
        value: v.removedArrayEntries.definitions as unknown as string[],
      });
    }
    diffState.definition = applyBackDiff(
      diffState.definition,
      (v.addedArrayEntries.definitions ?? []) as any,
      (v.removedArrayEntries.definitions ?? []) as any
    );

    accEditRows.push({
      type: 'enum',
      enumName: 'HaiDeathEnum',
      key: 'death',
      value: changedFields.death,
    });

    accEditRows.push({
      type: 'string',
      key: 'comment',
      value: changedFields.comment,
    });

    if (removedFields.comment !== undefined && removedFields.comment !== null) {
      accDeleteRows.push({
        type: 'string',
        key: 'comment',
        value: removedFields.comment,
      });
    }

    const filterNonEmptyRows = (rows: ParsedChangeRow[]) =>
      rows.filter((row) => {
        if (Array.isArray(row.value)) return Boolean(row.value.length);
        return row.value !== undefined && row.value !== null;
      });

    return {
      _id: reversedChangeLog.length - 1 - i,
      user: v.user,
      timestamp: v.timestamp,
      addRows: filterNonEmptyRows(accAddRows),
      editRows: filterNonEmptyRows(accEditRows),
      deleteRows: filterNonEmptyRows(accDeleteRows),
    };
  };

  const slice = showHistory ? reversedChangeLog : reversedChangeLog.slice(0, 1);
  return slice.map(mapItem);
}

function sortPathogensByLocale<T extends HaiPathogenDto | undefined>(pathogens: T[], locale: string): T[] {
  return ArrayUtils.sortedByLocaleString(pathogens, (v) => v?.label ?? '', locale, {caseFirst: 'lower'});
}

function formatChangeRow(
  key: number | string,
  formatters: Formatters,
  locale: string,
  pathogensData: HaiPathogenDto[],
  isJoinerVisible: boolean,
  row: ParsedChangeRow,
  muiStyles: any,
  getTranslationForStandardTypeId: (id: number) => string,
  getCodeLabelForCode: (standardTypeCode: string) => string | undefined,
  standardType: string
) {
  const {t, tEnum, formatDate, formatString} = formatters;
  let value = '' as string | undefined;
  let keyLabel = t(`hai.miniScreen.hai.${row.key}`);

  switch (row.type) {
    case 'date':
      value = formatDate(row.value) ?? '';
      break;
    case 'enum':
      value = tEnum(row.enumName, row.value) ?? '';
      if (row.enumName === 'InvasiveDevicePredefined') {
        keyLabel = t(`hai.miniScreen.hai.invasiveDevices`);
      }
      break;
    case 'enumArray':
      value = (row.value ?? []).map((v) => tEnum(row.enumName, v) ?? '').join(', ');
      if (row.enumName === 'InvasiveDevicePredefined') {
        keyLabel = t(`hai.miniScreen.hai.invasiveDevices`);
      }
      break;
    case 'agensArray': {
      const rowPathogens = row.value.map((v) => pathogensData.find((w) => w.code === v));
      const rowPathogensSorted = sortPathogensByLocale(rowPathogens, locale);
      value = rowPathogensSorted.map((v) => v?.label).join(', ');
      break;
    }
    case 'stringArray':
      value = (row.value ?? []).join(', ');
      break;
    case 'standardTypeId':
      value = getTranslationForStandardTypeId(row.value);
      if (standardType && standardType !== 'NO_TYPES') {
        keyLabel = t(`hai.miniScreen.hai.standardType.${standardType}`);
      }
      break;
    case 'standardType':
      value = getCodeLabelForCode ? getCodeLabelForCode(row.value) ?? row.value ?? '' : row.value;
      if (standardType && standardType !== 'NO_TYPES') {
        keyLabel = t(`hai.miniScreen.hai.standardType.${standardType}`);
      }
      break;
    default:
      value = String(row.value);
      break;
  }

  const joiner = formatString(t('hai.miniScreen.hai.changelog.edit.row'), {
    key: '',
    value: '',
  });

  return (
    <div key={key} data-name='change-row' css={muiStyles.changelogItemRow}>
      <span data-name='format.key' css={muiStyles.changelogItemHighlightKey}>
        {keyLabel}
      </span>
      <span css={muiStyles.changelogItemRight}>
        <span data-visible={isJoinerVisible} css={muiStyles.changelogItemJoiner}>
          {joiner}
        </span>
        <span data-name='format.value' css={muiStyles.changelogItemHighlightValue}>
          {value}
        </span>
      </span>
    </div>
  );
}

type HaiChangelogProps = {
  hai: HaidiSchema.components['schemas']['HAIDetailDto'] | undefined;
  isEditing: boolean;
  storageIdPrefix: string;
  muiStyles: any;
  pathogensData: HaiPathogenDto[];
  locale: string;
  getTranslationForStandardTypeId: (id: number) => string;
  getInvasiveDeviceLabel: (value: string) => string;
  getCodeLabelForCode: (standardTypeCode: string) => string | undefined;
  standardType: string;
};

const HaiChangelog: React.FC<HaiChangelogProps> = ({
  hai,
  isEditing,
  storageIdPrefix,
  muiStyles,
  pathogensData,
  locale,
  getTranslationForStandardTypeId,
  getInvasiveDeviceLabel,
  getCodeLabelForCode,
  standardType,
}) => {
  const [showHistory, _, setShowHistory] = useLocalStorage(`${storageIdPrefix}.showChangelogHistory`, false);
  const changelog: ParsedChange[] = useMemo(
    () => parseChanges(hai, showHistory || isEditing, getTranslationForStandardTypeId, getInvasiveDeviceLabel),
    [hai, showHistory, isEditing, getTranslationForStandardTypeId, getInvasiveDeviceLabel]
  );
  const formatters = useFormatters();
  const {t} = formatters;
  const haiId = hai?.id ?? 0;

  return (
    <span css={muiStyles.changelogWrapper}>
      <div css={muiStyles.changelog} data-name={`hai-${haiId}-changelog`}>
        {changelog.map((change, i) => (
          <HaiChangelogItem
            key={i}
            change={change}
            storageId={`${storageIdPrefix}.change-${change._id}.show`}
            muiStyles={muiStyles}
            formatters={formatters}
            locale={locale}
            pathogensData={pathogensData}
            getTranslationForStandardTypeId={getTranslationForStandardTypeId}
            getCodeLabelForCode={getCodeLabelForCode}
            standardType={standardType}
          />
        ))}
        <Collapse in={!isEditing && (hai?.changeLog?.length ?? 0) > 1}>
          <LightAccordionButtonHeader
            css={muiStyles.changelogHistoryButton}
            isOpen={showHistory}
            setIsOpen={setShowHistory}
            iconTitle=''
            data-name='expand-changelog-button'
          >
            <Typography variant='label'>
              {showHistory ? t('hai.miniScreen.hai.changelogHistory.close') : t('hai.miniScreen.hai.changelogHistory.open')}
            </Typography>
          </LightAccordionButtonHeader>
        </Collapse>
      </div>
    </span>
  );
};

type HaiChangelogItemProps = {
  change: ParsedChange;
  storageId: string;
  muiStyles: any;
  formatters: Formatters;
  locale: string;
  pathogensData: HaiPathogenDto[];
  getTranslationForStandardTypeId: (id: number) => string;
  getCodeLabelForCode: (standardTypeCode: string) => string | undefined;
  standardType: string;
};

const HaiChangelogItem: React.FC<HaiChangelogItemProps> = ({
  change,
  storageId,
  muiStyles,
  formatters,
  locale,
  pathogensData,
  getTranslationForStandardTypeId,
  getCodeLabelForCode,
  standardType,
}) => {
  const {t, formatDateTime} = formatters;
  const [showChangeRows, _, setShowChangeRows] = useLocalStorage(storageId, false);

  return (
    <div css={muiStyles.changelogItem} data-name={`change-item-${change._id}`}>
      <LightAccordionButtonHeader
        isOpen={showChangeRows}
        setIsOpen={setShowChangeRows}
        css={muiStyles.changelogItemHeader}
        data-name='change-header'
      >
        <div css={muiStyles.changelogItemHeaderLeft}>
          <DlMuiDynamicIcon
            iconName='haidiUserIcon'
            useFixedViewBox={12}
            inputCss={muiStyles.changelogItemHeaderIconButton}
            svgIconInputCss={muiStyles.changelogItemHeaderIcon}
            iconTitle={'hai.miniScreen.hai.changelogUser'}
          />
          <Typography css={muiStyles.changelogItemHeaderText} variant='label' data-name='change-name'>
            {change.user}
          </Typography>
          <div css={muiStyles.changelogItemHeaderSpacer} />
          <DlMuiDynamicIcon
            iconName='haidiTimeIcon'
            useFixedViewBox={12}
            inputCss={muiStyles.changelogItemHeaderIconButton}
            svgIconInputCss={muiStyles.changelogItemHeaderIcon}
            iconTitle={'hai.miniScreen.hai.changelogTime'}
          />
          <Typography css={muiStyles.changelogItemHeaderTextShrink} variant='label' data-name='change-date'>
            {formatDateTime(change.timestamp)}
          </Typography>
        </div>
      </LightAccordionButtonHeader>
      <Collapse in={showChangeRows}>
        <div css={muiStyles.changelogItemRows}>
          {Boolean(change.addRows.length) && (
            <div data-name='change-add-group' css={muiStyles.changelogItemRowGroup}>
              <span css={muiStyles.changelogItemRowGroupHeader}>{t('hai.miniScreen.hai.changelog.add')}</span>
              {change.addRows.map((changeRow, j) => {
                return formatChangeRow(
                  j,
                  formatters,
                  locale,
                  pathogensData,
                  false,
                  changeRow,
                  muiStyles,
                  getTranslationForStandardTypeId,
                  getCodeLabelForCode,
                  standardType
                );
              })}
            </div>
          )}
          {Boolean(change.editRows.length) && (
            <div data-name='change-edit-group' css={muiStyles.changelogItemRowGroup}>
              <span css={muiStyles.changelogItemRowGroupHeader}>{t('hai.miniScreen.hai.changelog.edit')}</span>
              {change.editRows.map((changeRow, j) => {
                return formatChangeRow(
                  j,
                  formatters,
                  locale,
                  pathogensData,
                  true,
                  changeRow,
                  muiStyles,
                  getTranslationForStandardTypeId,
                  getCodeLabelForCode,
                  standardType
                );
              })}
            </div>
          )}
          {Boolean(change.deleteRows.length) && (
            <div data-name='change-delete-group' css={muiStyles.changelogItemRowGroup}>
              <span css={muiStyles.changelogItemRowGroupHeader}>{t('hai.miniScreen.hai.changelog.delete')}</span>
              {change.deleteRows.map((changeRow, j) => {
                return formatChangeRow(
                  j,
                  formatters,
                  locale,
                  pathogensData,
                  false,
                  changeRow,
                  muiStyles,
                  getTranslationForStandardTypeId,
                  getCodeLabelForCode,
                  standardType
                );
              })}
            </div>
          )}
          {!(change.addRows.length || change.editRows.length || change.deleteRows.length) && (
            <span css={muiStyles.changelogItemRow}>{t('chart.emptyData')}</span>
          )}
        </div>
      </Collapse>
    </div>
  );
};
